import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabaseClient';
import LoadingSpinner from '../../components/LoadingSpinner';
import ChatActionsSidebar from '../../components/ChatActionsSidebar';
import { API_ENDPOINTS, BACKEND_NODE_URL } from '../../config/constants';
import '../../components/SettingsPage.css';
import '../../components/AtendimentosPage.css';
import AppIcon from '../../components/icons/AppIcon';
 

const AtendimentosPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [initialLoading, setInitialLoading] = useState(true);
  const [connections, setConnections] = useState([]);
  const [selectedConnectionId, setSelectedConnectionId] = useState(null);
  const [connOrder, setConnOrder] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [messages, setMessages] = useState({});
  const [novaMensagem, setNovaMensagem] = useState('');
  const [busca, setBusca] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [companyId, setCompanyId] = useState(null);
  // Paginação infinita de conversas
  const [conversationsOffset, setConversationsOffset] = useState(0);
  const [loadingMoreConversations, setLoadingMoreConversations] = useState(false);
  const [hasMoreConversations, setHasMoreConversations] = useState(true);
  const conversationsListRef = useRef(null);
  // Lightbox de mídia (imagens em tela cheia)
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [lightboxSrc, setLightboxSrc] = useState('');
  const [lightboxType, setLightboxType] = useState('');
  // Redimensionamento das colunas (lista de conversas vs área de mensagens)
  const [sidebarWidthPct, setSidebarWidthPct] = useState(26); // % inicial mais estreito
  const [isResizing, setIsResizing] = useState(false);
  const containerRef = useRef(null);
  // Cache simples para URLs assinadas do Storage
  const signedUrlCache = useRef(new Map());
  // Ref para o canal de mensagens real-time (para cleanup sem matar outros canais)
  const messagesChannelRef = useRef(null);
  // Gravação de áudio (mídia via microfone)
  const [isRecording, setIsRecording] = useState(false);
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  // Upload de imagem (ícone da câmera)
  const imageInputRef = useRef(null);
  // Imagem pendente (pré-visualização antes de enviar)
  const [pendingImage, setPendingImage] = useState(null); // { dataUrl, mime, name }
  // Upload de documento (ícone de anexo)
  const documentInputRef = useRef(null);
  // Documento pendente (pré-visualização antes de enviar)
  const [pendingDocument, setPendingDocument] = useState(null); // { dataUrl, mime, name, size }
  // Emojis
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const emojiPickerRef = useRef(null);
  const inputMsgRef = useRef(null);
  const EMOJIS = [
    '😀','😁','😂','🤣','😊','😍','😘','😜','🤩','🤔','🤨','😐','🙄','😴','🤤','😢','😭','😡',
    '👍','👎','🙏','👏','🙌','💪','🔥','✨','💖','🎉','✅','❌','⚠️','⭐','🌟','🌞','🌙','☔','☕',
    '🍕','🍔','🍻','🎯','📞','📷'
  ];

  // Menu de opções no cabeçalho da conversa (três pontos)
  const [showChatMenu, setShowChatMenu] = useState(false);
  const chatMenuRef = useRef(null);

  const insertEmoji = (emoji) => {
    try {
      setNovaMensagem((prev) => `${prev || ''}${emoji}`);
      if (inputMsgRef.current) inputMsgRef.current.focus();
    } catch (_) {}
  };

  const handleEditContact = async () => {
    try {
      if (!selectedConversationId) return;

      const conv = (conversations || []).find(c => c.id === selectedConversationId);
      let contactId = conv?.contatoId || conv?.contato_id || null;

      // Fallback: buscar vínculo direto no Supabase (o width/shape da lista pode não conter contatoId)
      if (!contactId) {
        try {
          const { data, error } = await supabase
            .from('conversas')
            .select('contato_id')
            .eq('id', selectedConversationId)
            .maybeSingle();
          if (!error && data?.contato_id) {
            contactId = data.contato_id;
          }
        } catch (_) {}
      }

      if (!contactId) {
        alert('Este atendimento não está vinculado a um contato cadastrado.');
        return;
      }

      navigate('/contatos', { state: { openContactId: contactId } });
    } catch (e) {
      console.error('[Atendimentos] Falha ao abrir detalhes do contato:', e);
      alert('Falha ao abrir detalhes do contato.');
    }
  };

  // Exibir/ocultar transcrição vinda do Supabase (sem disparar transcrição externa)
  const [showTranscriptionIds, setShowTranscriptionIds] = useState(new Set());

  // Utilitário local: manter apenas dígitos
  const onlyDigits = (v) => {
    try { return String(v || '').replace(/\D/g, ''); } catch (_) { return ''; }
  };

  // Salvar e abrir conversa: tenta backend e, se falhar, cria conversa local
  const handleSaveAndChat = async (payload) => {
    try {
      const allPhones = Array.isArray(payload?.phones) ? payload.phones : [];
      const plusPhones = allPhones.map(p => String(p||'').trim()).filter(p => p.startsWith('+'));
      const chosen = plusPhones[0] || allPhones[0] || null;
      if (!chosen) { alert('Nenhum telefone disponível para iniciar conversa.'); return; }
      const phoneDigits = onlyDigits(chosen);
      const name = payload?.name || phoneDigits || 'Contato';
      const avatar = payload?.avatar || null;

      // 1) Tenta backend
      try {
        const resp = await fetch(`${BACKEND_NODE_URL}/contacts/save-and-chat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            company_id: user?.company_id || null,
            connection_id: selectedConnectionId,
            name,
            phone: chosen,
            avatar,
          }),
        });
        const data = await resp.json().catch(() => ({}));
        if (resp.ok && data?.ok && data?.conversa_id) {
          const uuid = data.conversa_id;
          // 1) Buscar conversa recém-criada no Supabase e anexar na lista, se necessário
          try {
            const { data: conv, error: convErr } = await supabase
              .from('conversas')
              .select('id, conexao_id, contato_externo_id, contato_externo_nome, ultima_mensagem, ultima_mensagem_em, nao_lidas, image_url')
              .eq('id', uuid)
              .maybeSingle();
            if (!convErr && conv) {
              setConversations(prev => {
                if ((prev || []).some(c => c.id === uuid)) return prev;
                const novo = {
                  id: conv.id,
                  name: conv.contato_externo_nome || 'Conversa',
                  numero: (conv.contato_externo_id || '').replace(/@.*/,'') || '',
                  ultimaMensagem: conv.ultima_mensagem || 'Conversa iniciada',
                  hora: conv.ultima_mensagem_em || '',
                  naoLidas: conv.nao_lidas || 0,
                  image_url: conv.image_url || null,
                };
                return [...(prev || []), novo];
              });
            } else {
              // Fallback mínimo se a conversa ainda não for retornada
              setConversations(prev => {
                if ((prev || []).some(c => c.id === uuid)) return prev;
                return [...(prev || []), { id: uuid, name: name || 'Conversa', numero: phoneDigits, ultimaMensagem: 'Conversa iniciada', hora: '', naoLidas: 0, image_url: avatar || null }];
              });
            }
          } catch (_) {}
          // 2) Atualiza seleção e URL para deep-link estável
          setSelectedConversationId(uuid);
          try { navigate(`?conversa_id=${uuid}`, { replace: false }); } catch (_) {}
          return;
        }
        console.warn('[Atendimentos] save-and-chat backend falhou:', resp.status, data);
      } catch (e) {
        console.warn('[Atendimentos] save-and-chat backend exception:', e);
      }

      // 2) Fallback local
      const localId = `local-contact-${phoneDigits}`;
      setConversations(prev => {
        const exists = (prev || []).some(c => c.id === localId);
        if (exists) return prev;
        const novo = {
          id: localId,
          name,
          numero: phoneDigits,
          avatarUrl: avatar || null,
          rawImageUrl: avatar || null,
          ultimaMensagem: '',
          hora: '',
          naoLidas: 0,
        };
        return [...(prev || []), novo];
      });
      setSelectedConversationId(localId);
    } catch (e) {
      console.warn('[Atendimentos] handleSaveAndChat erro:', e);
    }
  };

  const handleTranscribeClick = (msg) => {
    try {
      if (!msg) return;
      const telegramMeta = msg?.metadados?.telegram || {};
      const transcription = String(msg.transcricao || telegramMeta.transcription || msg.texto || '').trim();
      if (!transcription) {
        alert('Sem transcrição disponível para esta mensagem ainda.');
        return;
      }
      setShowTranscriptionIds(prev => {
        const n = new Set(prev);
        if (n.has(msg.id)) n.delete(msg.id); else n.add(msg.id);
        return n;
      });
    } catch (e) {
      console.warn('[Atendimentos] handleTranscribeClick erro:', e);
    }
  };

  useEffect(() => {
    const onDocClick = (e) => {
      try {
        // Fecha Emoji Picker
        if (showEmojiPicker && emojiPickerRef.current && !emojiPickerRef.current.contains(e.target)) {
          setShowEmojiPicker(false);
        }
        // Fecha menu de três pontos do chat
        if (showChatMenu && chatMenuRef.current && !chatMenuRef.current.contains(e.target)) {
          setShowChatMenu(false);
        }
      } catch (_) {}
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [showEmojiPicker, showChatMenu]);

  // Carregar preferência de largura da barra lateral do usuário
  useEffect(() => {
    try {
      const raw = localStorage.getItem('atendimentos_sidebar_width_pct');
      if (!raw) return;
      const v = Number(raw);
      if (Number.isFinite(v)) {
        const bounded = Math.max(20, Math.min(60, v));
        setSidebarWidthPct(bounded);
      }
    } catch (_) {}
  }, []);

  // Debug helper (desativado por padrão para evitar logs sensíveis)
  const DEBUG_MEDIA = false;
  const dlog = (...args) => { if (DEBUG_MEDIA) try { console.debug('[Atendimentos][media]', ...args); } catch (_) {} };

  // ==========================
  // Perfil do usuário (company_id)
  // ==========================
  useEffect(() => {
    (async () => {
      try {
        if (!user?.id) return;
        const { data, error } = await supabase
          .from('profiles')
          .select('company_id')
          .eq('id', user.id)
          .maybeSingle();
        if (!error && data?.company_id) setCompanyId(data.company_id);
      } catch (_) {}
    })();
  }, [user?.id]);

  // Carregar/ajustar ordem das conexões por companyId
  useEffect(() => {
    try {
      if (!companyId) return;
      const key = `atendimentos_conn_order_${companyId}`;
      const saved = localStorage.getItem(key);
      if (saved) {
        const arr = JSON.parse(saved);
        if (Array.isArray(arr)) setConnOrder(arr);
      }
    } catch (_) {}
  }, [companyId]);

  // Quando a lista de conexões muda, garante que todos os IDs estejam na ordem
  useEffect(() => {
    try {
      if (!Array.isArray(connections) || connections.length === 0) return;
      const ids = connections.map(c => c.id);
      setConnOrder(prev => {
        const base = Array.isArray(prev) && prev.length > 0 ? prev.filter(id => ids.includes(id)) : [];
        const missing = ids.filter(id => !base.includes(id));
        const next = [...base, ...missing];
        // Persistir se companyId carregado
        if (companyId) {
          try { localStorage.setItem(`atendimentos_conn_order_${companyId}`, JSON.stringify(next)); } catch (_) {}
        }
        return next;
      });
    } catch (_) {}
  }, [connections, companyId]);

  const orderedConnections = useMemo(() => {
    try {
      if (!Array.isArray(connections) || connections.length === 0) return [];
      if (!Array.isArray(connOrder) || connOrder.length === 0) return connections;
      const indexById = new Map(connOrder.map((id, idx) => [id, idx]));
      return [...connections].sort((a, b) => {
        const ia = indexById.has(a.id) ? indexById.get(a.id) : Number.MAX_SAFE_INTEGER;
        const ib = indexById.has(b.id) ? indexById.get(b.id) : Number.MAX_SAFE_INTEGER;
        return ia - ib;
      });
    } catch (_) { return connections || []; }
  }, [connections, connOrder]);

  const onTabDragStart = (e, dragIndex) => {
    try {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', String(dragIndex));
    } catch (_) {}
  };

  const onTabDragOver = (e) => {
    try { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; } catch (_) {}
  };

  const onTabDrop = (e, dropIndex) => {
    try {
      e.preventDefault();
      const src = Number(e.dataTransfer.getData('text/plain'));
      if (!Number.isFinite(src)) return;
      const ids = orderedConnections.map(c => c.id);
      if (src < 0 || src >= ids.length || dropIndex < 0 || dropIndex >= ids.length) return;
      if (src === dropIndex) return;
      const next = [...ids];
      const [moved] = next.splice(src, 1);
      next.splice(dropIndex, 0, moved);
      setConnOrder(next);
      if (companyId) {
        try { localStorage.setItem(`atendimentos_conn_order_${companyId}`, JSON.stringify(next)); } catch (_) {}
      }
    } catch (_) {}
  };

  // ==========================
  // Busca de contatos (tempo real enquanto digita)
  // ==========================
  useEffect(() => {
    const q = (busca || '').trim();
    if (q.length < 2 || !companyId) { setSearchResults([]); return; }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        setIsSearching(true);
        const like = `%${q}%`;
        // Tabela contatos: colunas reais -> nome, numero (texto), image_url
        // Construir filtros OR em nome e numero (ambos ilike)
        const filters = [`nome.ilike.${like}`, `numero.ilike.${like}`];
        const orFilter = filters.join(',');
        // Tabela assumida: contatos (ajuste se seu schema usar outro nome)
        const { data, error } = await supabase
          .from('contatos')
          .select('id, nome, numero, image_url')
          .eq('company_id', companyId)
          .or(orFilter)
          .limit(20);
        if (!cancelled) {
          if (error) {
            console.warn('[Atendimentos] busca contatos erro:', error?.message || error);
            setSearchResults([]);
          } else {
            setSearchResults(data || []);
          }
        }
      } catch (e) {
        if (!cancelled) setSearchResults([]);
      } finally {
        if (!cancelled) setIsSearching(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [busca, companyId]);

  const handleSelectContact = (contact) => {
    try {
      // Procurar conversa existente por telefone ou id de contato (heurística)
      const byPhone = (conversations || []).find(c => String(c.phone || c.telefone || c.number || c.numero || '').replace(/\D/g,'') === String(contact.numero || '').replace(/\D/g,''));
      if (byPhone) {
        setSelectedConversationId(byPhone.id);
        setBusca('');
        setSearchResults([]);
        return;
      }
      const byName = (conversations || []).find(c => (c.name || c.nome || '').trim().toLowerCase() === String(contact.nome || '').trim().toLowerCase());
      if (byName) {
        setSelectedConversationId(byName.id);
        setBusca('');
        setSearchResults([]);
        return;
      }
      // Não existe conversa: criar placeholder local
      const localId = `local-conv-${contact.id}`;
      const displayName = contact.nome || contact.numero || 'Contato';
      setConversations(prev => {
        const exists = (prev || []).some(c => c.id === localId);
        if (exists) return prev;
        return [
          ...prev,
          {
            id: localId,
            name: displayName,
            numero: contact.numero || '',
            image_url: contact.image_url || null,
            hora: '',
            ultimaMensagem: '',
          }
        ];
      });
      setSelectedConversationId(localId);
      setBusca('');
      setSearchResults([]);
    } catch (e) {}
  };

  // Marca último evento recebido via Realtime (para fallback de polling)
  const lastRealtimeEventTs = useRef(0);

  // Helpers para deduplicar mensagens locais (id "local-*") quando chegam as oficiais via Realtime
  const isLocalMsg = (m) => {
    try { return typeof m?.id === 'string' && m.id.startsWith('local-'); } catch (_) { return false; }
  };

  // ==========================
  // Separadores de Dia na Conversa (estilo WhatsApp/Telegram)
  // ==========================
  const getMsgDate = (msg) => {
    try {
      if (msg?.criado_em) return new Date(msg.criado_em);
      if (msg?.timestamp_provedor) return new Date(msg.timestamp_provedor);
      if (typeof msg?.tempTs === 'number') return new Date(msg.tempTs);
    } catch (_) {}
    return new Date(); // fallback seguro
  };

  const sameDay = (d1, d2) => {
    try {
      return d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate();
    } catch (_) { return false; }
  };

  const formatDayLabel = (date) => {
    try {
      const today = new Date();
      const yesterday = new Date();
      yesterday.setDate(today.getDate() - 1);
      if (sameDay(date, today)) return 'Hoje';
      if (sameDay(date, yesterday)) return 'Ontem';
      // dd/MM/yyyy
      const dd = String(date.getDate()).padStart(2, '0');
      const mm = String(date.getMonth() + 1).padStart(2, '0');
      const yyyy = date.getFullYear();
      return `${dd}/${mm}/${yyyy}`;
    } catch (_) { return ''; }
  };
  const sameKind = (a, b) => {
    const aAudio = !!a?.audioUrl;
    const bAudio = !!b?.audioUrl;
    const aImage = !!a?.imageUrl;
    const bImage = !!b?.imageUrl;
    const aVideo = !!a?.videoUrl;
    const bVideo = !!b?.videoUrl;
    const bothAudio = aAudio && bAudio;
    const bothImage = aImage && bImage;
    const bothVideo = aVideo && bVideo;
    const bothText = !aAudio && !aImage && !aVideo && !bAudio && !bImage && !bVideo;
    return bothAudio || bothImage || bothVideo || bothText;
  };
  const shouldRemoveLocal = (local, official) => {
    try {
      if (!isLocalMsg(local)) return false;
      if (sameKind(local, official)) return true;
      // Heurística adicional: se ambos são enviados (saída) e muito próximos no tempo, considere o mesmo envio
      const tLocal = Number(local.tempTs || 0);
      const tNow = Date.now();
      const near = tLocal > 0 && (tNow - tLocal) < 120000; // 2 minutos
      const bothOutgoing = !!local.enviada && !!official.enviada;
      // Se o oficial é imagem/áudio (tipo_conteudo) e o local possui mídia correspondente (mesmo que o oficial ainda não tenha URL)
      const tipo = String(official?.tipoConteudo || official?.tipo_conteudo || '').toLowerCase();
      const officialLooksImage = tipo.startsWith('image');
      const officialLooksAudio = tipo === 'audio' || tipo.startsWith('audio/') || tipo === 'ptt' || tipo === 'myaudio';
      if (bothOutgoing && near) {
        if (officialLooksImage && !!local.imageUrl) return true;
        if (officialLooksAudio && !!local.audioUrl) return true;
        // Texto com igual conteúdo
        const t1 = (local.texto || '').trim();
        const t2 = (official.texto || '').trim();
        if (t1 && t2 && t1 === t2) return true;
      }
      return false;
    } catch (_) { return false; }
  };
  const dedupeLocals = (list, official) => {
    let removed = false;
    return list.filter(m => {
      if (removed) return true;
      if (shouldRemoveLocal(m, official)) { removed = true; return false; }
      return true;
    });
  };

  // Mescla mensagens oficiais (arr) com existentes, removendo locais correspondentes
  const mergeWithLocals = (arr, existing) => {
    const locals = (existing || []).filter(isLocalMsg);
    // Para cada oficial, remover locais equivalentes
    let remainingLocals = [...locals];
    arr.forEach(off => {
      remainingLocals = remainingLocals.filter(l => !shouldRemoveLocal(l, off));
    });
    return [...arr, ...remainingLocals];
  };

  // Lê conversa_id da URL (quando navegado a partir da página de contatos)
  const location = useLocation();
  const conversaIdFromUrl = (() => {
    try {
      const qs = new URLSearchParams(location.search);
      const v = qs.get('conversa_id');
      // Mantém UUID/string; evita Number() que resulta em NaN para UUIDs
      return v || null;
    } catch (_) {
      return null;
    }
  })();

  // Stubs locais para evitar dependência de serviços legados
  const getAuthToken = () => null;
  const getUserData = () => {
    try {
      const raw = localStorage.getItem('astro_user_data');
      return raw ? JSON.parse(raw) : null;
    } catch (_) {
      return null;
    }
  };

  // ==========================
  // Envio de Imagem (ícone da câmera)
  // ==========================
  const openImagePicker = () => {
    if (imageInputRef.current) imageInputRef.current.click();
  };

  const openDocumentPicker = () => {
    if (documentInputRef.current) documentInputRef.current.click();
  };

  const fileToDataUrl = (file) => new Promise((resolve, reject) => {
    try {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    } catch (e) { reject(e); }
  });

  const onDocumentSelected = async (e) => {
    try {
      const file = e.target.files?.[0];
      if (!file) return;
      
      // Validar tamanho (máx 100MB)
      const maxSize = 100 * 1024 * 1024;
      if (file.size > maxSize) {
        alert('Arquivo muito grande. Tamanho máximo: 100MB');
        return;
      }
      
      const dataUrl = await fileToDataUrl(file);
      setPendingDocument({ 
        dataUrl, 
        mime: file.type || 'application/octet-stream', 
        name: file.name || 'documento',
        size: file.size
      });
    } catch (err) {
      console.warn('[Atendimentos] Erro ao selecionar documento:', err?.message || err);
      alert('Erro ao carregar documento.');
    } finally {
      try { if (documentInputRef.current) documentInputRef.current.value = ''; } catch (_) {}
    }
  };

  const onImageSelected = async (e) => {
    try {
      const files = e?.target?.files;
      if (!files || files.length === 0) return;
      const file = files[0];
      // Permitir apenas tipos suportados pelo WhatsApp API para imagens
      const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!allowed.includes(file.type)) {
        alert('Formato de imagem não suportado. Use JPG, PNG ou WEBP.');
        return;
      }
      const dataUrl = await fileToDataUrl(file);
      // Apenas preparar pré-visualização; envio ocorrerá no submit junto do texto
      setPendingImage({ dataUrl, mime: file.type || 'image/jpeg', name: file.name || 'imagem' });
    } catch (err) {
      console.warn('[Atendimentos] Erro ao enviar imagem:', err?.message || err);
      alert('Erro ao enviar imagem.');
    } finally {
      try { if (imageInputRef.current) imageInputRef.current.value = ''; } catch (_) {}
    }
  };

  // ==========================
  // Gravação e envio de Áudio (microfone)
  // ==========================
  const blobToDataUrl = (blob) => new Promise((resolve, reject) => {
    try {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    } catch (e) { reject(e); }
  });

  const sendRecordedAudio = async (blob) => {
    try {
      if (!blob) return;
      if (!selectedConversationId || !selectedConnectionId) {
        console.warn('[Atendimentos] Sem conversa/conexão selecionada para enviar áudio');
        return;
      }
      const dataUrl = await blobToDataUrl(blob);
      // Usar ptt (mensagem de voz) por padrão
      const mediaType = 'ptt';
      const payload = {
        connection_id: selectedConnectionId,
        conversation_id: selectedConversationId,
        type: mediaType,
        file: dataUrl,
        // Ex.: opções adicionais: readchat: true, delay: 800, replyid: '...' etc.
      };
      // Não adiciona mensagem otimista para áudio - deixa webhook processar
      // (áudio precisa ser processado antes de exibir)
      const controller = new AbortController();
      const tm = setTimeout(() => controller.abort(), 30000);
      const resp = await fetch(API_ENDPOINTS.CONNECTIONS_SEND_MEDIA, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });
      clearTimeout(tm);
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        console.warn('[Atendimentos] Falha ao enviar áudio (ptt) via backend/Uazapi:', resp.status, data);
        alert('Falha ao enviar áudio.');
        // Remove mensagem local se falhar
        setMessages(prev => {
          const existing = prev[selectedConversationId] || [];
          const locals = existing.filter(isLocalMsg);
          let merged = [...existing];
          locals.forEach(l => {
            merged = dedupeLocals(merged, l);
          });
          return { ...prev, [selectedConversationId]: merged.filter(m => m.id !== tempId) };
        });
        return;
      }
      try { if (DEBUG_MEDIA) console.debug('[Atendimentos] Áudio (ptt) enviado provider_response:', data?.provider_response); } catch (_) {}
    } catch (err) {
      console.warn('[Atendimentos] Erro ao enviar áudio (ptt):', err?.message || err);
      alert('Erro ao enviar áudio.');
      // Não conseguir enviar: nenhuma alteração adicional (entrada otimista permanece?)
    }
  };

  const handleMicClick = async () => {
    try {
      if (!window.isSecureContext) {
        alert('Este recurso requer contexto seguro (HTTPS). Abra via https://app.astrodev.com.br');
        return;
      }
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        alert('Seu navegador não suporta captura de microfone. Tente Chrome/Firefox em HTTPS.');
        return;
      }
      if (!isRecording) {
        // Iniciar gravação
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        recordedChunksRef.current = [];
        const preferOgg = typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported('audio/ogg; codecs=opus');
        const mimeType = preferOgg ? 'audio/ogg; codecs=opus' : 'audio/webm';
        const mr = new MediaRecorder(stream, { mimeType });
        mr.ondataavailable = (e) => {
          if (e.data && e.data.size > 0) recordedChunksRef.current.push(e.data);
        };
        mr.onstop = async () => {
          try {
            const blob = new Blob(recordedChunksRef.current, { type: mimeType });
            await sendRecordedAudio(blob);
          } catch (e) {
            console.warn('[Atendimentos] Falha ao processar áudio gravado:', e);
          }
        };
        mediaRecorderRef.current = mr;
        mr.start();
        setIsRecording(true);
      } else {
        // Parar gravação
        const mr = mediaRecorderRef.current;
        if (mr && mr.state !== 'inactive') {
          mr.stop();
        }
        setIsRecording(false);
        // Parar tracks do stream
        try {
          const tracks = mr?.stream?.getTracks?.() || [];
          tracks.forEach(t => t.stop());
        } catch (_) {}
      }
    } catch (err) {
      console.warn('[Atendimentos] Erro ao acessar microfone/gravar:', err?.message || err);
      alert('Não foi possível acessar o microfone. Verifique as permissões.');
    }
  };

  // Resolve URL exibível para vídeos (somente Storage ou url_midia do backend)
  const resolveDisplayVideo = async (row) => {
    try {
      const type = String(row?.tipo_conteudo || '').toLowerCase();
      const json = parseConteudoJson(row?.conteudo);
      const jsonMime = String(json?.mimetype || '').toLowerCase();
      const looksVideo = type.startsWith('video') || jsonMime.startsWith('video');
      if (!looksVideo) return { url: null, mime: null, poster: null };

      const isEncUrl = (u) => {
        if (!u || typeof u !== 'string') return false;
        const low = u.toLowerCase();
        return /\.enc(\?|$)/.test(low) || low.includes('mmg.whatsapp.net') && /\/v\//.test(low);
      };
      const effectiveMime = type.startsWith('video') ? type : (json?.mimetype || 'video/mp4');
      // 1) caminho_storage
      if (row?.caminho_storage) {
        const ref = parseStorageRef(row.caminho_storage);
        if (ref) {
          const signed = await getSignedUrl(ref.bucket, ref.path, 3600);
          if (signed) { dlog('resolveDisplayVideo:storage', { id: row?.id, ref, signed }); return { url: signed, mime: effectiveMime, poster: null }; }
        }
      }
      // 2) url_midia direta (recusa .enc)
      if (row?.url_midia && /^https?:\/\//i.test(String(row.url_midia)) && !isEncUrl(row.url_midia)) {
        dlog('resolveDisplayVideo:url_midia', { id: row?.id, url: row.url_midia });
        return { url: row.url_midia, mime: effectiveMime, poster: null };
      }
      // Poster a partir de JPEGThumbnail (preview)
      const poster = json?.JPEGThumbnail ? `data:image/jpeg;base64,${json.JPEGThumbnail}` : null;
      if (poster) dlog('resolveDisplayVideo:poster', { id: row?.id, poster: true });
      return { url: null, mime: effectiveMime, poster };
    } catch (_) {
      return { url: null, mime: null, poster: null };
    }
  };

  // Atualiza mídia de uma mensagem específica a partir do banco (busca url_midia/caminho_storage e resolve URLs)
  const refreshMessageMedia = async (messageId) => {
    try {
      if (!messageId || !selectedConversationId) return;
      const { data, error } = await supabase
        .from('mensagens')
        .select('id, conteudo, tipo_conteudo, url_midia, caminho_storage, direcao, criado_em, timestamp_provedor, id_provedor, transcricao, from_me, numero, canal')
        .eq('id', messageId)
        .maybeSingle();
      if (error || !data) { dlog('refreshMessageMedia:fetch-error', { messageId, error }); return; }
      dlog('refreshMessageMedia:fetched', { id: data.id, tipo: data.tipo_conteudo, hasUrl: !!data.url_midia, hasStorage: !!data.caminho_storage });
      const tipo = data.tipo_conteudo || 'texto';
      const mediaAudio = await resolvePlayableMedia(data);
      const mediaImage = await resolveDisplayImage(data);
      const mediaVideo = await resolveDisplayVideo(data);
      dlog('refreshMessageMedia:resolved', { id: data.id, audio: !!mediaAudio.url, image: !!mediaImage.url, video: !!mediaVideo.url, poster: !!mediaVideo.poster });
      setMessages(prev => {
        const arr = prev[selectedConversationId] || [];
        const idx = arr.findIndex(m => m.id === data.id);
        const base = {
          id: data.id,
          texto: data.conteudo,
          tipoConteudo: tipo,
          enviada: (data.direcao === 'saida' || data.direcao === 'outgoing'),
          hora: data.criado_em ? new Date(data.criado_em).toLocaleTimeString() : (data.timestamp_provedor ? new Date(data.timestamp_provedor).toLocaleTimeString() : ''),
          criado_em: data.criado_em || null,
          timestamp_provedor: data.timestamp_provedor || null,
          audioUrl: mediaAudio.url || null,
          audioMime: mediaAudio.mime || null,
          imageUrl: mediaImage.url || null,
          imageMime: mediaImage.mime || null,
          id_provedor: data.id_provedor || null,
          transcricao: data.transcricao || null,
          metadados: data.metadados || null,
        };
        if (idx === -1) return { ...prev, [selectedConversationId]: [...arr, base] };
        const novoArr = [...arr];
        novoArr[idx] = { ...novoArr[idx], ...base };
        return { ...prev, [selectedConversationId]: novoArr };
      });
    } catch (e) {
      console.warn('[Atendimentos] refreshMessageMedia failed:', e);
    }
  };

  const [downloadingMediaIds, setDownloadingMediaIds] = useState(new Set());

  const requestFullMediaDownload = async (mensagemId) => {
    if (!mensagemId) return;
    setDownloadingMediaIds(prev => new Set(prev).add(mensagemId));
    try {
      // Usar endpoint centralizado para evitar bater no servidor do Vite (5173)
      dlog('requestFullMediaDownload:start', { mensagemId, endpoint: API_ENDPOINTS.MEDIA_REPROCESS });
      let resp = await fetch(API_ENDPOINTS.MEDIA_REPROCESS, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mensagem_id: mensagemId })
      });
      let data = await resp.json().catch(() => ({}));
      dlog('requestFullMediaDownload:response', { status: resp?.status, data });
      if (!resp.ok || !data?.ok) {
        dlog('requestFullMediaDownload:error', { status: resp?.status, data });
      }
      if (!resp || !resp.ok || !data?.ok) {
        console.warn('[Atendimentos] Falha no reprocessamento de mídia:', data?.error || (resp ? resp.statusText : 'sem resposta'));
      } else {
        // Se backend devolveu caminho_storage, assina e atualiza imediatamente; senão, faz refresh no banco
        if (data.caminho_storage) {
          const ref = parseStorageRef(data.caminho_storage);
          if (ref) {
            const signed = await getSignedUrl(ref.bucket, ref.path, 3600);
            if (signed) {
              dlog('requestFullMediaDownload:got-signed', { mensagemId, signed });
              setMessages(prev => {
                const arr = prev[selectedConversationId] || [];
                const idx = arr.findIndex(m => m.id === mensagemId);
                if (idx === -1) return prev;
                const novoArr = [...arr];
                const mime = (data.mimetype || '').toLowerCase();
                if (mime.startsWith('image/')) {
                  novoArr[idx] = { ...novoArr[idx], imageUrl: signed, imageMime: mime || novoArr[idx].imageMime || 'image/jpeg', tipoConteudo: mime || novoArr[idx].tipoConteudo || 'image/jpeg' };
                } else if (mime.startsWith('video/')) {
                  novoArr[idx] = { ...novoArr[idx], videoUrl: signed, videoMime: mime || novoArr[idx].videoMime || 'video/mp4', tipoConteudo: mime || novoArr[idx].tipoConteudo || 'video/mp4' };
                } else if (mime.startsWith('audio/')) {
                  novoArr[idx] = { ...novoArr[idx], audioUrl: signed, audioMime: mime || novoArr[idx].audioMime || 'audio/mpeg', tipoConteudo: mime || novoArr[idx].tipoConteudo || 'audio/mpeg' };
                }
                return { ...prev, [selectedConversationId]: novoArr };
              });
              return;
            }
          }
        }
        // Fallback: atualizar mídia dessa mensagem consultando o banco
        await refreshMessageMedia(mensagemId);
      }
      if (!resp.ok || !data?.ok) {
        console.warn('[Atendimentos] Falha no reprocessamento de mídia:', data?.error || resp.statusText);
      } else {
        // Se backend devolveu caminho_storage, assina e atualiza imediatamente; senão, faz refresh no banco
        if (data.caminho_storage) {
          const ref = parseStorageRef(data.caminho_storage);
          if (ref) {
            const signed = await getSignedUrl(ref.bucket, ref.path, 3600);
            if (signed) {
              setMessages(prev => {
                const arr = prev[selectedConversationId] || [];
                const idx = arr.findIndex(m => m.id === mensagemId);
                if (idx === -1) return prev;
                const novoArr = [...arr];
                const mime = (data.mimetype || '').toLowerCase();
                if (mime.startsWith('image/')) {
                  novoArr[idx] = { ...novoArr[idx], imageUrl: signed, imageMime: mime || novoArr[idx].imageMime || 'image/jpeg', tipoConteudo: mime || novoArr[idx].tipoConteudo || 'image/jpeg' };
                } else if (mime.startsWith('video/')) {
                  novoArr[idx] = { ...novoArr[idx], videoUrl: signed, videoMime: mime || novoArr[idx].videoMime || 'video/mp4', tipoConteudo: mime || novoArr[idx].tipoConteudo || 'video/mp4' };
                } else if (mime.startsWith('audio/')) {
                  novoArr[idx] = { ...novoArr[idx], audioUrl: signed, audioMime: mime || novoArr[idx].audioMime || 'audio/mpeg', tipoConteudo: mime || novoArr[idx].tipoConteudo || 'audio/mpeg' };
                }
                return { ...prev, [selectedConversationId]: novoArr };
              });
              return;
            }
          }
        }
        // Fallback: atualizar mídia dessa mensagem consultando o banco
        await refreshMessageMedia(mensagemId);
      }
    } catch (e) {
      console.warn('[Atendimentos] Erro ao solicitar reprocessamento de mídia:', e);
    } finally {
      setDownloadingMediaIds(prev => {
        const n = new Set(prev);
        n.delete(mensagemId);
        return n;
      });
    }
  };

  // Resolve URL exibível para imagens (Storage assinado, url_midia direta ou preview JPEGThumbnail)
  const resolveDisplayImage = async (row) => {
    try {
      const type = String(row?.tipo_conteudo || '').toLowerCase();
      const json = parseConteudoJson(row?.conteudo);
      const jsonMime = String(json?.mimetype || '').toLowerCase();
      const isEncUrl = (u) => {
        if (!u || typeof u !== 'string') return false;
        const low = u.toLowerCase();
        return /\.enc(\?|$)/.test(low);
      };
      const guessMimeFromUrl = (u) => {
        try {
          const low = String(u || '').toLowerCase();
          if (low.endsWith('.jpg') || low.endsWith('.jpeg')) return 'image/jpeg';
          if (low.endsWith('.png')) return 'image/png';
          if (low.endsWith('.webp')) return 'image/webp';
          if (low.endsWith('.gif')) return 'image/gif';
          return null;
        } catch (_) { return null; }
      };
      const effectiveMime = type.startsWith('image') ? type : (json?.mimetype || 'image/jpeg');

      // 0) Só considerar imagem se o tipo indicar imagem ou a URL sugerir imagem por extensão
      const gmFromUrl = row?.url_midia ? guessMimeFromUrl(row.url_midia) : null;
      const looksImage = type.startsWith('image') || jsonMime.startsWith('image') || !!gmFromUrl;
      if (!looksImage) {
        dlog('resolveDisplayImage:not-image', { id: row?.id, type, jsonMime, url: row?.url_midia });
        return { url: null, mime: null };
      }

      // 0.1) Se url_midia já existir (data URL ou HTTP) e não for .enc
      if (row?.url_midia && !isEncUrl(row.url_midia)) {
        const urlMidia = String(row.url_midia).trim();
        if (/^data:image\//i.test(urlMidia)) {
          const mime = urlMidia.split(';')[0].replace(/^data:/, '') || effectiveMime;
          dlog('resolveDisplayImage:url_midia_data', { id: row?.id, mime });
          return { url: urlMidia, mime };
        }
        if (/^https?:\/\//i.test(urlMidia)) {
          if (gmFromUrl) {
            const gm = gmFromUrl || effectiveMime;
            dlog('resolveDisplayImage:url_midia', { id: row?.id, url: row.url_midia, gm });
            return { url: row.url_midia, mime: gm };
          }
        }
      }
      // 1) caminho_storage (assinar e inferir mime)
      if (row?.caminho_storage) {
        const ref = parseStorageRef(row.caminho_storage);
        if (ref) {
          const signed = await getSignedUrl(ref.bucket, ref.path, 3600);
          if (signed) {
            const gm = guessMimeFromUrl(signed) || effectiveMime;
            // Somente retornar se a URL assinada aparentar ser imagem
            if (gm && gm.startsWith('image/')) {
              dlog('resolveDisplayImage:storage', { id: row?.id, ref, signed, gm });
              return { url: signed, mime: gm };
            }
          }
        }
      }
      // 2) Tentar data JPEGThumbnail (preview provisório)
      {
        const thumb = json?.JPEGThumbnail ? `data:image/jpeg;base64,${json.JPEGThumbnail}` : null;
        if (thumb) { dlog('resolveDisplayImage:thumb', { id: row?.id }); return { url: thumb, mime: 'image/jpeg' }; }
      }
      dlog('resolveDisplayImage:unresolved', { id: row?.id });
      return { url: null, mime: null };
    } catch (_) {
      return { url: null, mime: null };
    }
  };

  // Auto-refresh: para mensagens que chegaram com payload JSON e sem audioUrl ainda,
  // tentar reprocessar periodicamente por um curto período (ex.: 60s) até o backend popular url_midia/caminho_storage
  useEffect(() => {
    if (!selectedConversationId) return;
    let attempts = 0;
    const timer = setInterval(() => {
      attempts += 1;
      const list = messages[selectedConversationId] || [];
      const pendentes = list.filter((m) => {
        const tipo = (m.tipoConteudo || 'texto').toLowerCase();
        const isJsonText = (typeof m.texto === 'string' && m.texto.trim().startsWith('{') && m.texto.trim().endsWith('}'));
        const looksAudioTipo = tipo === 'audio' || tipo.startsWith('audio/') || tipo === 'ptt' || tipo === 'myaudio';
        return !m.audioUrl && (isJsonText || looksAudioTipo);
      }).slice(0, 3); // refrescar no máx. 3 por ciclo
      if (pendentes.length > 0) {
        pendentes.forEach(p => refreshMessageMedia(p.id));
      }
      if (attempts >= 12) { // ~1 min em intervalos de 5s
        clearInterval(timer);
      }
    }, 5000);
    return () => clearInterval(timer);
  }, [selectedConversationId, messages]);

  // Tenta parsear JSON do campo conteudo (quando providers enviam um objeto serializado)
  const parseConteudoJson = (texto) => {
    try {
      if (!texto || typeof texto !== 'string') return null;
      const t = texto.trim();
      if (!(t.startsWith('{') && t.endsWith('}'))) return null;
      const obj = JSON.parse(t);
      return obj && typeof obj === 'object' ? obj : null;
    } catch (_) {
      return null;
    }
  };

  // Resolve uma URL reproduzível para mídias (prioriza caminho no Storage, depois url_midia, depois JSON no conteudo)
  const resolvePlayableMedia = async (row) => {
    try {
      const type = String(row?.tipo_conteudo || '').toLowerCase();
      const json = parseConteudoJson(row?.conteudo);
      const jsonMime = String(json?.mimetype || '').toLowerCase();
      const guessMimeFromUrl = (u) => {
        try {
          const low = String(u || '').toLowerCase();
          if (low.endsWith('.ogg')) return 'audio/ogg';
          if (low.endsWith('.opus')) return 'audio/ogg; codecs=opus';
          if (low.endsWith('.webm')) return 'audio/webm';
          if (low.endsWith('.mp3') || low.includes('.mpeg')) return 'audio/mpeg';
          if (low.endsWith('.wav')) return 'audio/wav';
          if (low.endsWith('.m4a')) return 'audio/mp4';
          return null;
        } catch (_) { return null; }
      };
      const gmFromUrl = row?.url_midia ? guessMimeFromUrl(row.url_midia) : null;
      const looksAudio = (type.startsWith('audio') || jsonMime.startsWith('audio') || type === 'ptt' || type === 'myaudio' || !!gmFromUrl);
      if (!looksAudio) { dlog('resolvePlayableMedia:not-audio', { id: row?.id, type, jsonMime, url: row?.url_midia }); return { url: null, mime: null }; }

      const effectiveMime = type.startsWith('audio') ? type : (json?.mimetype || 'audio/mpeg');
      // 0) Guard-rails: ignorar apenas URLs criptografadas (.enc) do WhatsApp
      const isEncUrl = (u) => {
        if (!u || typeof u !== 'string') return false;
        const low = u.toLowerCase();
        return /\.enc(\?|$)/.test(low);
      };
      // 0.1) Se o conteúdo já trouxer um dataURL de áudio (ex.: data:audio/ogg;base64,...) usar diretamente
      const maybeDataUrl = () => {
        try {
          const txt = row?.conteudo || '';
          if (typeof txt === 'string' && /^data:audio\//i.test(txt.trim())) {
            return { url: txt.trim(), mime: (txt.split(';')[0] || 'audio/mpeg').replace(/^data:/, '') };
          }
          if (json && typeof json.dataUrl === 'string' && /^data:audio\//i.test(json.dataUrl)) {
            const mt = json.mimetype || (json.dataUrl.split(';')[0] || 'audio/mpeg').replace(/^data:/, '');
            return { url: json.dataUrl, mime: mt };
          }
          return null;
        } catch (_) { return null; }
      };
      const dataUrlHit = maybeDataUrl();
      if (dataUrlHit) { dlog('resolvePlayableMedia:dataurl', { id: row?.id, mime: dataUrlHit.mime }); return dataUrlHit; }
      // 0.2) Se já houver url_midia e a extensão indicar áudio, usar diretamente
      if (row?.url_midia && /^https?:\/\//i.test(String(row.url_midia)) && !isEncUrl(row.url_midia)) {
        const gm = gmFromUrl || effectiveMime;
        dlog('resolvePlayableMedia:url_midia-audio-like', { id: row?.id, url: row.url_midia, gm });
        return { url: row.url_midia, mime: gm };
      }

      // 1) caminho_storage (bucket:path ou URL do storage)
      if (row?.caminho_storage) {
        const ref = parseStorageRef(row.caminho_storage);
        if (ref) {
          const signed = await getSignedUrl(ref.bucket, ref.path, 3600);
          if (signed) {
            const gm = guessMimeFromUrl(signed) || effectiveMime;
            if (gm && gm.startsWith('audio')) {
              dlog('resolvePlayableMedia:storage', { id: row?.id, ref, signed, gm });
              return { url: signed, mime: gm };
            }
          }
        }
      }
      // 2) url_midia direta (recusar .enc) — fallback geral
      if (row?.url_midia && /^https?:\/\//i.test(String(row.url_midia)) && !isEncUrl(row.url_midia)) {
        const gm = guessMimeFromUrl(row.url_midia) || effectiveMime;
        dlog('resolvePlayableMedia:url_midia', { id: row?.id, url: row.url_midia, gm });
        return { url: row.url_midia, mime: gm };
      }
      // 3) JSON no conteudo com { URL|url|audioUrl|audioURL|fileURL, mimetype } (recusar .enc)
      if (json) {
        const k = json.URL || json.url || json.audioUrl || json.audioURL || json.fileURL;
        if (k && /^https?:\/\//i.test(String(k)) && !isEncUrl(k)) {
          const mt = json.mimetype || effectiveMime || 'audio/mpeg';
          dlog('resolvePlayableMedia:jsonURL', { id: row?.id, url: k, mt });
          return { url: k, mime: mt };
        }
      }
      dlog('resolvePlayableMedia:unresolved', { id: row?.id });
      return { url: null, mime: null };
    } catch (_) {
      return { url: null, mime: null };
    }
  };

  // Detecta e extrai referência de Storage (bucket + path) de uma URL/caminho
  const parseStorageRef = (url) => {
    try {
      if (!url || typeof url !== 'string') return null;
      const raw = String(url).trim();
      // Rejeitar padrões claramente externos mesmo sem esquema (ex.: 'https/pps.whatsapp.net/...')
      if (/^(https?|ftp)\//i.test(raw)) return null;
      if (/whatsapp\.net|fbcdn\.net|cdn\.|googleusercontent\.com|akamaihd\.net/i.test(raw)) return null;
      // Se contém esquema (ex.: http://, https://, gs://), só aceite se for padrão do Supabase Storage
      if (/^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//.test(raw)) {
        const m = raw.match(/\/storage\/v1\/object\/(public\/)?([^/]+)\/(.+)$/);
        if (!m) {
          // URL externa (ex.: pps.whatsapp.net) -> não assinamos
          return null;
        }
        // URL de storage válida: continuar fluxo abaixo (aproveitar regex já aplicado)
      }
      // Caso 1: URL padrão do Supabase Storage
      // Ex: https://<project>.supabase.co/storage/v1/object/public/<bucket>/<path>
      // ou  https://<project>.supabase.co/storage/v1/object/<bucket>/<path>
      const m = raw.match(/\/storage\/v1\/object\/(public\/)?([^/]+)\/(.+)$/);
      if (m) {
        const bucket = m[2];
        const path = m[3];
        return { bucket, path };
      }
      // Caso 2: referência curta "bucket:path" ou "bucket/dir/file" (somente se NÃO houver esquema)
      if (raw.includes(':')) {
        const [bucket, ...rest] = raw.split(':');
        const path = rest.join(':').replace(/^\//, '');
        if (bucket && path) return { bucket, path };
      }
      if (!raw.startsWith('http') && raw.includes('/')) {
        const [bucket, ...rest] = raw.split('/');
        const path = rest.join('/');
        if (bucket && path) return { bucket, path };
      }
      return null;
    } catch (_) {
      return null;
    }
  };

  // Obtém URL assinada do Storage via backend (seguro)
  const getSignedUrl = async (bucket, path, expiresIn = 3600) => {
    const key = `${bucket}:${path}`;
    const now = Date.now();
    const cached = signedUrlCache.current.get(key);
    if (cached && cached.expiresAt > now + 5000) {
      return cached.url;
    }
    try {
      const caminho_storage = `${bucket}:${path}`;
      const response = await fetch(API_ENDPOINTS.MEDIA_SIGNED_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          caminho_storage,
          empresa_id: companyId 
        })
      });
      
      if (!response.ok) return null;
      
      const data = await response.json();
      const url = data?.signedUrl || null;
      
      if (url) {
        signedUrlCache.current.set(key, { url, expiresAt: now + (data.expiresIn || expiresIn) * 1000 });
      }
      return url;
    } catch (err) {
      console.error('[getSignedUrl] Erro:', err);
      return null;
    }
  };

  // Carregar conexões conectadas da empresa (Supabase) e cachear
  useEffect(() => {
    let isMounted = true;
    const loadConnections = async () => {
      try {
        // Exibe rapidamente cache local se existir
        const cached = localStorage.getItem('active_connections');
        if (cached) {
          try {
            const parsed = JSON.parse(cached);
            if (isMounted) {
              setConnections(Array.isArray(parsed) ? parsed : []);
              if (Array.isArray(parsed) && parsed.length > 0) setSelectedConnectionId((prev) => prev || parsed[0]?.id || null);
            }
          } catch (_) {}
        }

        // Requisita do Supabase se tivermos company_id
        const cid = user?.company_id;
        if (!cid) {
          return;
        }
        const { data, error } = await supabase
          .from('connections')
          .select('id, name, type, status, whatsapp_api_status, number')
          .eq('company_id', cid)
          // considerar variações de status e whatsapp_api_status como conectados (case-insensitive)
          .or('status.ilike.conectado,whatsapp_api_status.ilike.conectado');
        if (error) {
          console.error('[Atendimentos] Erro ao buscar conexões no Supabase:', error);
          return;
        }
        const list = Array.isArray(data) ? data : [];
        // Normaliza nome para UI
        const ui = list.map((r) => ({
          id: r.id,
          name: r.name || 'Conexão',
          tipo: r.type || 'whatsapp',
          status: ((r.whatsapp_api_status || r.status || 'conectado') + '').toLowerCase(),
          number: r.number || '-',
        }));
        if (!isMounted) return;
        setConnections(ui);
        try { localStorage.setItem('active_connections', JSON.stringify(ui)); } catch (_) {}
        if (ui.length > 0) setSelectedConnectionId((prev) => prev || ui[0].id);
      } catch (e) {
        console.warn('[Atendimentos] Falha ao carregar conexões');
      } finally {
        if (isMounted) setInitialLoading(false);
      }
    };
    loadConnections();
    return () => { isMounted = false; };
  }, [user?.company_id]);

  // Realtime: manter a lista de conversas atualizada (última mensagem no topo)
  useEffect(() => {
    if (!selectedConnectionId) return;
    const channel = supabase
      .channel(`rt_conversas_${selectedConnectionId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'conversas',
          filter: `conexao_id=eq.${selectedConnectionId}`,
        },
        (payload) => {
          const row = payload?.new;
          if (!row) return;
          setConversations(prev => {
            // se já existir, não duplica; senão adiciona e reordena
            const exists = prev.some(c => c.id === row.id);
            const ts = row.ultima_mensagem_em ? Date.parse(row.ultima_mensagem_em) : 0;
            const base = exists ? prev.map(c => c.id === row.id ? {
              ...c,
              ultimaMensagem: row.ultima_mensagem ?? c.ultimaMensagem,
              hora: row.ultima_mensagem_em ? new Date(row.ultima_mensagem_em).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : c.hora,
              ultimaTs: ts || c.ultimaTs || 0,
              naoLidas: row.nao_lidas ?? c.naoLidas,
            } : c) : [{
              id: row.id,
              nome: row.contato_externo_nome || row.contato_externo_id || 'Contato',
              numero: row.contato_externo_id || '-',
              avatarUrl: row.image_url || null,
              rawImageUrl: row.image_url || null,
              ultimaMensagem: row.ultima_mensagem || '',
              hora: row.ultima_mensagem_em ? new Date(row.ultima_mensagem_em).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : '',
              ultimaTs: ts,
              naoLidas: row.nao_lidas || 0,
            }, ...prev];
            // reordena por ultimaTs desc
            const sorted = [...base].sort((a, b) => (Number(b.ultimaTs||0) - Number(a.ultimaTs||0)));
            try { localStorage.setItem(`conversations_${selectedConnectionId}`, JSON.stringify(sorted)); } catch (_) {}
            return sorted;
          });
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'conversas',
          filter: `conexao_id=eq.${selectedConnectionId}`,
        },
        (payload) => {
          const row = payload?.new;
          if (!row) return;
          setConversations(prev => {
            const ts = row.ultima_mensagem_em ? Date.parse(row.ultima_mensagem_em) : 0;
            const updated = prev.map(c => c.id === row.id ? {
              ...c,
              nome: row.contato_externo_nome || row.contato_externo_id || c.nome,
              numero: row.contato_externo_id || c.numero,
              avatarUrl: row.image_url || c.avatarUrl,
              rawImageUrl: row.image_url || c.rawImageUrl,
              ultimaMensagem: row.ultima_mensagem ?? c.ultimaMensagem,
              hora: row.ultima_mensagem_em ? new Date(row.ultima_mensagem_em).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : c.hora,
              ultimaTs: ts || c.ultimaTs || 0,
              naoLidas: row.nao_lidas ?? c.naoLidas,
            } : c);
            // reordena por ultimaTs desc
            const sorted = [...updated].sort((a, b) => (Number(b.ultimaTs||0) - Number(a.ultimaTs||0)));
            try { localStorage.setItem(`conversations_${selectedConnectionId}`, JSON.stringify(sorted)); } catch (_) {}
            return sorted;
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [selectedConnectionId]);

  // Polling de conversas como fallback (quando Realtime falha) a cada 12s
  useEffect(() => {
    if (!selectedConnectionId) return;
    let stop = false;
    const iv = setInterval(async () => {
      if (stop) return;
      try {
        const { data, error } = await supabase
          .from('conversas')
          .select('id, conexao_id, contato_id, contato_externo_id, contato_externo_nome, ultima_mensagem, ultima_mensagem_em, nao_lidas, image_url')
          .eq('conexao_id', selectedConnectionId)
          .order('ultima_mensagem_em', { ascending: false, nullsFirst: false })
          .limit(50);
        if (error) return;
        const mapped = (data || []).map(row => ({
          id: row.id,
          nome: row.contato_externo_nome || row.contato_externo_id || 'Contato',
          numero: row.contato_externo_id || '-',
          avatarUrl: row.image_url || null,
          rawImageUrl: row.image_url || null,
          ultimaMensagem: row.ultima_mensagem || '',
          hora: row.ultima_mensagem_em ? new Date(row.ultima_mensagem_em).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : '',
          ultimaTs: row.ultima_mensagem_em ? Date.parse(row.ultima_mensagem_em) : 0,
          naoLidas: row.nao_lidas || 0,
        }));
        setConversations(mapped);
        try { localStorage.setItem(`conversations_${selectedConnectionId}`, JSON.stringify(mapped)); } catch (_) {}
      } catch (_) {}
    }, 12000);
    return () => { stop = true; clearInterval(iv); };
  }, [selectedConnectionId]);

  // Helper: mapear conversas para o shape esperado pela UI
  const mapConversationsToUI = async (arr, nomesPorId = {}, imagensPorId = {}, nomesPorNumero = {}, imagensPorNumero = {}, imagensPorNumeroNormalizado = {}) => {
    return arr.map((c) => ({
      id: c.id,
      contatoId: c.contato_id || null,
      nome: (c.contato_id && nomesPorId[c.contato_id]) || nomesPorNumero[c.contato_externo_id] || c.contato_externo_nome || c.contato_externo_id,
      numero: c.contato_externo_id,
      avatarUrl: normalizeAvatarUrl((c.contato_id && imagensPorId[c.contato_id])
        || imagensPorNumero[c.contato_externo_id]
        || imagensPorNumeroNormalizado[String(c.contato_externo_id || '').replace(/\D/g, '')]
        || c.image_url
        || null),
      rawImageUrl: normalizeAvatarUrl(c.image_url || null),
      ultimaMensagem: c.ultima_mensagem || '',
      hora: c.ultima_mensagem_em ? new Date(c.ultima_mensagem_em).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : '',
      naoLidas: c.nao_lidas || 0,
    }));
  };

  // Helper: buscar dados de contatos em lote
  const fetchContactsData = async (arr) => {
    let nomesPorId = {};
    let imagensPorId = {};
    let nomesPorNumero = {};
    let imagensPorNumero = {};
    let imagensPorNumeroNormalizado = {};
    
    const idsContato = [...new Set(arr.map((c) => c?.contato_id).filter(Boolean))];
    const numerosContato = [...new Set(arr.filter(c => !c?.contato_id && c?.contato_externo_id).map(c => c.contato_externo_id))];
    
    // Busca por ID
    if (idsContato.length > 0) {
      try {
        let q1 = supabase
          .from('contatos')
          .select('id, nome, image_url')
          .in('id', idsContato);
        if (user?.company_id) q1 = q1.eq('company_id', user.company_id);
        const { data: contatosById, error: err1 } = await q1;
        if (!err1 && Array.isArray(contatosById)) {
          contatosById.forEach(it => {
            nomesPorId[it.id] = it.nome || null;
            imagensPorId[it.id] = it.image_url || null;
          });
        }
      } catch (e) {
        console.warn('[Atendimentos] Falha ao buscar contatos por id:', e);
      }
    }
    
    // Busca por número
    if (numerosContato.length > 0) {
      try {
        let q2 = supabase
          .from('contatos')
          .select('numero, nome, image_url')
          .not('numero', 'is', null);
        if (user?.company_id) q2 = q2.eq('company_id', user.company_id);
        const { data: contatosByNumero, error: err2 } = await q2;
        if (!err2 && Array.isArray(contatosByNumero)) {
          contatosByNumero.forEach(it => {
            if (it.numero) {
              nomesPorNumero[it.numero] = it.nome || null;
              imagensPorNumero[it.numero] = it.image_url || null;
              const onlyDigits = String(it.numero).replace(/\D/g, '');
              if (onlyDigits) imagensPorNumeroNormalizado[onlyDigits] = it.image_url || null;
            }
          });
        }
      } catch (e) {
        console.warn('[Atendimentos] Falha ao buscar contatos por numero:', e);
      }
    }
    
    return { nomesPorId, imagensPorId, nomesPorNumero, imagensPorNumero, imagensPorNumeroNormalizado };
  };

  // Função para carregar mais conversas (paginação infinita)
  const loadMoreConversations = async () => {
    if (loadingMoreConversations || !hasMoreConversations || !selectedConnectionId) return;
    
    setLoadingMoreConversations(true);
    try {
      const LOAD_MORE_LIMIT = 20;
      const currentOffset = conversations.length;
      
      const { data, error } = await supabase
        .from('conversas')
        .select('id, conexao_id, contato_id, contato_externo_id, contato_externo_nome, ultima_mensagem, ultima_mensagem_em, nao_lidas, image_url')
        .eq('conexao_id', selectedConnectionId)
        .order('ultima_mensagem_em', { ascending: false, nullsFirst: false })
        .range(currentOffset, currentOffset + LOAD_MORE_LIMIT - 1);
      
      if (error) {
        console.error('[Atendimentos] Erro ao carregar mais conversas:', error);
        return;
      }
      
      const arr = Array.isArray(data) ? data : [];
      
      if (arr.length === 0) {
        setHasMoreConversations(false);
        return;
      }
      
      if (arr.length < LOAD_MORE_LIMIT) {
        setHasMoreConversations(false);
      }
      
      // Buscar dados de contatos
      const contactsData = await fetchContactsData(arr);
      const mapped = await mapConversationsToUI(arr, ...Object.values(contactsData));
      
      // Adicionar às conversas existentes (evitando duplicatas)
      setConversations(prev => {
        const existingIds = new Set(prev.map(c => c.id));
        const newConvs = mapped.filter(c => !existingIds.has(c.id));
        return [...prev, ...newConvs];
      });
      
    } catch (e) {
      console.warn('[Atendimentos] Falha ao carregar mais conversas');
    } finally {
      setLoadingMoreConversations(false);
    }
  };

  // Handler de scroll para paginação infinita
  const handleConversationsScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    // Carregar mais quando estiver a 100px do final
    if (scrollHeight - scrollTop - clientHeight < 100) {
      loadMoreConversations();
    }
  };

  // ✅ NOVO: Quando há conversa_id na URL, buscar a conversa e selecionar sua conexão
  useEffect(() => {
    if (!conversaIdFromUrl) return;
    
    const loadConversationFromUrl = async () => {
      try {
        // Buscar a conversa pelo ID
        const { data: conversa, error } = await supabase
          .from('conversas')
          .select('id, conexao_id, contato_id, contato_externo_id, contato_externo_nome')
          .eq('id', conversaIdFromUrl)
          .single();

        if (error) {
          console.error('[Atendimentos] Erro ao buscar conversa da URL:', error);
          return;
        }

        if (conversa && conversa.conexao_id) {
          // Selecionar a conexão automaticamente
          setSelectedConnectionId(conversa.conexao_id);
          // A conversa será selecionada automaticamente quando as conversas carregarem
        }
      } catch (e) {
        console.error('[Atendimentos] Erro ao carregar conversa da URL');
      }
    };

    loadConversationFromUrl();
  }, [conversaIdFromUrl]);

  // Quando a conexão selecionada mudar, buscar as últimas 50 conversas no Supabase
  useEffect(() => {
    if (!selectedConnectionId) return;
    
    // Reset paginação ao trocar de conexão
    setConversationsOffset(0);
    setHasMoreConversations(true);
    
    let cancelled = false;
    const loadConversations = async () => {
      try {
        const { data, error } = await supabase
          .from('conversas')
          .select('id, conexao_id, contato_id, contato_externo_id, contato_externo_nome, ultima_mensagem, ultima_mensagem_em, nao_lidas, image_url')
          .eq('conexao_id', selectedConnectionId)
          .order('ultima_mensagem_em', { ascending: false, nullsFirst: false })
          .limit(50);
        if (error) {
          console.error('[Atendimentos] Erro ao buscar conversas no Supabase:', error);
          return;
        }
        const arr = Array.isArray(data) ? data : [];
        
        // Se retornou menos de 50, não há mais para carregar
        if (arr.length < 50) {
          setHasMoreConversations(false);
        }
        
        // Buscar dados de contatos
        const contactsData = await fetchContactsData(arr);
        const mapped = await mapConversationsToUI(arr, ...Object.values(contactsData));
        
        if (cancelled) return;
        setConversations(mapped);
        
        // Resolve URLs privadas do Storage assinando-as de forma assíncrona
        (async () => {
          try {
            const refs = mapped
              .map(c => ({ id: c.id, url: c.avatarUrl || c.rawImageUrl }))
              .filter(x => !!x.url && !isPublicStorageUrl(x.url))
              .map(x => ({ id: x.id, url: x.url, ref: parseStorageRef(x.url) }))
              .filter(x => x.ref);
            if (refs.length === 0) return;
            const results = await Promise.all(refs.map(async ({ id, ref }) => {
              const signed = await getSignedUrl(ref.bucket, ref.path, 3600);
              return { id, signed };
            }));
            const signedMap = new Map(results.filter(r => r.signed).map(r => [r.id, r.signed]));
            if (signedMap.size > 0 && !cancelled) {
              setConversations(prev => prev.map(c => signedMap.has(c.id) ? { ...c, avatarUrl: signedMap.get(c.id) } : c));
            }
          } catch (e) {
            console.warn('[Atendimentos] Falha ao assinar URLs de avatar:', e);
          }
        })();
        try { localStorage.setItem(`conversations_${selectedConnectionId}`, JSON.stringify(mapped)); } catch (_) {}
        
        // Preserva a seleção atual; se não houver seleção e existir conversaIdFromUrl, seleciona-a.
        if (!selectedConversationId) {
          if (conversaIdFromUrl) {
            setSelectedConversationId(conversaIdFromUrl);
          }
        }
      } catch (e) {
        console.warn('[Atendimentos] Falha ao carregar conversas');
        setConversations([]);
      }
    };
    loadConversations();
    return () => { cancelled = true; };
  }, [selectedConnectionId, conversaIdFromUrl]);

  // Configura a subscrição em tempo real para as mensagens
  useEffect(() => {
    if (!selectedConversationId) return;

    // Evita tentar assinar canal para IDs locais (não UUID)
    if (String(selectedConversationId).startsWith('local-contact-')) {
      return;
    }
    
    // Remover apenas o canal de mensagens anterior (não matar canal de conversas)
    if (messagesChannelRef.current) {
      supabase.removeChannel(messagesChannelRef.current);
      messagesChannelRef.current = null;
    }
    
    // Configura o canal de subscrição para a conversa atual
    const channel = supabase
      .channel(`realtime_messages_${selectedConversationId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'mensagens',
          filter: `conversa_id=eq.${selectedConversationId}`
        },
        async (payload) => {
          
          // Atualiza o estado baseado no tipo de evento
          if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
            try {
              const newMessage = payload.new;
              
              // Processa a mídia da mensagem (se houver)
              const tipo = newMessage.tipo_conteudo || 'texto';
              const [mediaAudio, mediaImage, mediaVideo] = await Promise.all([
                resolvePlayableMedia(newMessage),
                resolveDisplayImage(newMessage),
                resolveDisplayVideo(newMessage)
              ]);
              
              const processedMessage = {
                id: newMessage.id,
                texto: newMessage.conteudo,
                tipoConteudo: tipo,
                audioUrl: mediaAudio?.url || null,
                audioMime: mediaAudio?.mime || null,
                imageUrl: mediaImage?.url || null,
                imageMime: mediaImage?.mime || null,
                videoUrl: mediaVideo?.url || null,
                videoMime: mediaVideo?.mime || null,
                videoPoster: mediaVideo?.poster || null,
                caminho_storage: newMessage.caminho_storage || null,
                enviada: (newMessage.direcao === 'saida' || newMessage.direcao === 'outgoing'),
                hora: newMessage.criado_em 
                  ? new Date(newMessage.criado_em).toLocaleTimeString() 
                  : (newMessage.timestamp_provedor 
                      ? new Date(newMessage.timestamp_provedor).toLocaleTimeString() 
                      : ''),
                criado_em: newMessage.criado_em || null,
                timestamp_provedor: newMessage.timestamp_provedor || null,
                status: 'sent',
                id_provedor: newMessage.id_provedor || null,
                transcricao: newMessage.transcricao || null,
                metadados: newMessage.metadados || null,
              };
              
              console.log('[REALTIME] Mensagem processada:', processedMessage);
              
              // Log especial para áudio
              if (processedMessage.tipoConteudo?.includes('audio')) {
                console.log('[REALTIME] ÁUDIO recebido:', {
                  id: processedMessage.id,
                  audioUrl: processedMessage.audioUrl,
                  enviada: processedMessage.enviada
                });
              }
              
              setMessages(prev => {
                const currentMessages = prev[selectedConversationId] || [];
                
                // PRIMEIRO: Verifica se a mensagem já existe para evitar duplicatas
                const messageExists = currentMessages.some(msg => msg.id === newMessage.id);
                if (messageExists) {
                  return prev;
                }
                
                // Se for UPDATE, atualiza mensagem existente
                if (payload.eventType === 'UPDATE') {
                  const existingIndex = currentMessages.findIndex(msg => msg.id === newMessage.id);
                  if (existingIndex !== -1) {
                    const updated = [...currentMessages];
                    updated[existingIndex] = processedMessage;
                    return {
                      ...prev,
                      [selectedConversationId]: updated
                    };
                  }
                  // Se não encontrou, adiciona como nova
                }
                
                // Se for mensagem enviada (fromMe=true), substituir mensagem otimista
                if (processedMessage.enviada) {
                  // Buscar mensagem otimista (local-*)
                  const optimisticIndex = currentMessages.findIndex((msg) => {
                    if (!String(msg.id).startsWith('local-')) return false;
                    const tLocal = String(msg.texto ?? '').trim();
                    const tNew = String(processedMessage.texto ?? '').trim();
                    if (tLocal && tNew) return tLocal === tNew;
                    if (msg.tipoConteudo && processedMessage.tipoConteudo) {
                      return msg.tipoConteudo === processedMessage.tipoConteudo;
                    }
                    return false;
                  });
                  
                  if (optimisticIndex !== -1) {
                    console.log('[REALTIME] ✅ Encontrou mensagem otimista no índice:', optimisticIndex);
                    
                    // Para áudio, só substitui se já tiver URL processada
                    const isAudio = processedMessage.tipoConteudo?.includes('audio');
                    console.log('[REALTIME] É áudio?', isAudio, 'Tem audioUrl?', !!processedMessage.audioUrl);
                    
                    if (isAudio && !processedMessage.audioUrl) {
                      console.log('[REALTIME] ⏳ Áudio ainda não processado, mantendo otimista');
                      // Áudio ainda não processado, ignora esta notificação
                      return prev;
                    }
                    
                    console.log('[REALTIME] ✅ Substituindo mensagem otimista pela real');
                    // Substituir mensagem otimista pela real
                    const updated = [...currentMessages];
                    updated[optimisticIndex] = processedMessage;
                    return {
                      ...prev,
                      [selectedConversationId]: updated
                    };
                  } else {
                    console.log('[REALTIME] ❌ Não encontrou mensagem otimista');
                  }
                }
                
                // Adicionar nova mensagem
                return {
                  ...prev,
                  [selectedConversationId]: [...currentMessages, processedMessage]
                };
              });
            } catch (error) {
              console.error('[REALTIME] Erro ao processar mensagem:', error);
              // Adiciona a mensagem mesmo com erro de processamento de mídia
              setMessages(prev => {
                const currentMessages = prev[selectedConversationId] || [];
                if (!currentMessages.some(msg => msg.id === payload.new.id)) {
                  return {
                    ...prev,
                    [selectedConversationId]: [...currentMessages, {
                      id: payload.new.id,
                      texto: payload.new.conteudo,
                      tipoConteudo: payload.new.tipo_conteudo || 'texto',
                      enviada: (payload.new.direcao === 'saida' || payload.new.direcao === 'outgoing'),
                      hora: payload.new.criado_em 
                        ? new Date(payload.new.criado_em).toLocaleTimeString() 
                        : (payload.new.timestamp_provedor 
                            ? new Date(payload.new.timestamp_provedor).toLocaleTimeString() 
                            : ''),
                      criado_em: payload.new.criado_em || null,
                      timestamp_provedor: payload.new.timestamp_provedor || null,
                      status: 'error',
                      error: 'Erro ao carregar mídia',
                      id_provedor: payload.new.id_provedor || null,
                      transcricao: payload.new.transcricao || null,
                      metadados: payload.new.metadados || null,
                    }]
                  };
                }
                return prev;
              });
            }
          }
        }
      )
      .subscribe((status) => {
        console.log(`[REALTIME] Subscription mensagens status: ${status} (conversa: ${selectedConversationId})`);
      });

    // Salvar referência do canal para cleanup específico
    messagesChannelRef.current = channel;

    // Função de limpeza para cancelar a subscrição quando o componente for desmontado
    return () => {
      supabase.removeChannel(channel);
      messagesChannelRef.current = null;
    };
  }, [selectedConversationId]);

  // Busca as mensagens iniciais da conversa selecionada (Supabase)
  useEffect(() => {
    if (!selectedConversationId) return;
    // Evita query no Supabase com conversa_id não UUID
    if (String(selectedConversationId).startsWith('local-contact-')) return;
    
    let cancelled = false;
    const loadMessages = async () => {
      try {
        const { data, error, count } = await supabase
          .from('mensagens')
          .select('id, conversa_id, direcao, conteudo, tipo_conteudo, url_midia, caminho_storage, timestamp_provedor, id_provedor, transcricao, metadados, criado_em, from_me, numero, canal')
          .eq('conversa_id', selectedConversationId)
          .order('criado_em', { ascending: true })
          .limit(100);
          
        if (error) {
          console.error('[Atendimentos] Erro ao carregar mensagens:', error);
          return;
        }
        // Processa mantendo a ordem natural (mais antigas primeiro)
        const arr = await Promise.all((data || []).map(async (m) => {
          const tipo = m.tipo_conteudo || 'texto';
          // Resolver mídia: áudio e imagem
          const mediaAudio = await resolvePlayableMedia(m);
          const mediaImage = await resolveDisplayImage(m);
          const mediaVideo = await resolveDisplayVideo(m);
          return {
            id: m.id,
            texto: m.conteudo,
            tipoConteudo: tipo,
            audioUrl: mediaAudio.url || null,
            audioMime: mediaAudio.mime || null,
            imageUrl: mediaImage.url || null,
            imageMime: mediaImage.mime || null,
            videoUrl: mediaVideo.url || null,
            videoMime: mediaVideo.mime || null,
            videoPoster: mediaVideo.poster || null,
            caminho_storage: m.caminho_storage || null,
            enviada: (m.direcao === 'saida' || m.direcao === 'outgoing'),
            hora: m.criado_em ? new Date(m.criado_em).toLocaleTimeString() : (m.timestamp_provedor ? new Date(m.timestamp_provedor).toLocaleTimeString() : ''),
            criado_em: m.criado_em || null,
            timestamp_provedor: m.timestamp_provedor || null,
            status: 'sent',
            id_provedor: m.id_provedor || null,
            transcricao: m.transcricao || null,
            metadados: m.metadados || null,
          };
        }));
        
        // ✅ FILTRAR ÁUDIOS DUPLICADOS
        // Áudios automáticos salvos duas vezes têm source: 'initial_audio' ou 'followup_audio' no metadados
        // Manter apenas o primeiro (mais antigo) e remover duplicatas
        const deduplicatedArr = (() => {
          const seen = new Map(); // Map de (tipo_conteudo + source) -> primeira mensagem
          const result = [];
          
          for (const msg of arr) {
            const isAutoAudio = msg.tipoConteudo === 'audio' && (msg.metadados?.source === 'initial_audio' || msg.metadados?.source === 'followup_audio');
            
            if (isAutoAudio) {
              const source = msg.metadados?.source;
              const key = `audio_${source}_${msg.enviada}`;
              if (!seen.has(key)) {
                seen.set(key, msg.id);
                result.push(msg);
              }
            } else {
              result.push(msg);
            }
          }
          
          return result;
        })();
        if (cancelled) {
          console.log('[DEBUG] Carregamento de mensagens cancelado');
          return;
        }
        
        console.log(`[DEBUG] Mensagens processadas (${deduplicatedArr.length}/${arr.length}):`, deduplicatedArr);
        
        // Mescla com locais removendo duplicatas por tipo
        setMessages(prev => {
          const existing = prev[selectedConversationId] || [];
          const locals = existing.filter(isLocalMsg);
          let merged = [...deduplicatedArr];
          locals.forEach(l => {
            merged = dedupeLocals(merged, l);
            merged.push(l);
          });
          return { ...prev, [selectedConversationId]: merged };
        });
        try { localStorage.setItem(`messages_${selectedConversationId}`, JSON.stringify(deduplicatedArr)); } catch (_) {}
      } catch (e) {
        console.warn('[Atendimentos] Falha ao carregar mensagens:', e);
      }
    };
    loadMessages();
    return () => { cancelled = true; };
  }, [selectedConversationId]);

  // Um único canal Realtime para mensagens (realtime_messages_* acima). Havia um segundo
  // canal duplicado que lia `messages` com closure desatualizada e competia com o primeiro,
  // causando sumiço da bolha otimista ao enviar.

  // Realtime: assinar updates em 'conversas' para refletir renomeações e metadados
  useEffect(() => {
    if (!selectedConnectionId) return;

    const channel = supabase
      .channel(`rt_conversas_${selectedConnectionId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'conversas',
          filter: `conexao_id=eq.${selectedConnectionId}`,
        },
        (payload) => {
          const row = payload?.new;
          if (!row) return;
          setConversations((prev) => {
            const idx = prev.findIndex((c) => c.id === row.id);
            if (idx === -1) return prev;
            const atualizado = {
              ...prev[idx],
              contatoId: row.contato_id ?? prev[idx].contatoId,
              nome: row.contato_externo_nome || row.contato_externo_id || prev[idx].nome,
              numero: row.contato_externo_id || prev[idx].numero,
              ultimaMensagem: (row.ultima_mensagem ?? prev[idx].ultimaMensagem),
              hora: row.ultima_mensagem_em
                ? new Date(row.ultima_mensagem_em).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
                : prev[idx].hora,
              naoLidas: (row.nao_lidas ?? prev[idx].naoLidas),
              // Atualiza avatar quando a conversa receber image_url (por enrich)
              avatarUrl: row.image_url || prev[idx].avatarUrl || null,
            };
            const arr = [...prev];
            arr[idx] = atualizado;
            return arr;
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [selectedConnectionId]);

  // Realtime: assinar updates na tabela 'contatos' e refletir nomes nas conversas
  useEffect(() => {
    // Caso não haja conversas carregadas, não assina
    if (!selectedConnectionId || conversations.length === 0) return;

    const channel = supabase
      .channel('rt_contatos_renomeacao')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'contatos',
        },
        (payload) => {
          const row = payload?.new;
          if (!row?.id) return;
          // Se alguma conversa usa esse contato, atualiza o nome
          setConversations((prev) => {
            let changed = false;
            const atualizado = prev.map((c) => {
              // Atualiza por match do contatoId (UUID) ou por numero (texto)
              if ((c.contatoId && c.contatoId === row.id) || (c.numero && row.numero && c.numero === row.numero)) {
                changed = true;
                return {
                  ...c,
                  nome: row.nome || c.nome,
                  // Se o contato recebeu/alterou image_url, refletir no avatar
                  avatarUrl: row.image_url || c.avatarUrl || null,
                };
              }
              return c;
            });
            return changed ? atualizado : prev;
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [selectedConnectionId, conversations]);

  const selecionarConversa = async (id) => {
    setSelectedConversationId(id);
    try {
      const conv = conversations.find(c => c.id === id);
      if (!conv) return;
      // Buscar imagem do contato diretamente no Supabase para refletir imediatamente
      let imageUrl = null;
      if (conv.contatoId) {
        try {
          let q = supabase
            .from('contatos')
            .select('image_url, nome, numero')
            .eq('id', conv.contatoId);
          if (user?.company_id) q = q.eq('company_id', user.company_id);
          const { data, error } = await q.maybeSingle();
          if (!error && data) imageUrl = data.image_url || null;
        } catch (_) {}
      }
      if (!imageUrl && conv.numero) {
        try {
          let qn = supabase
            .from('contatos')
            .select('image_url, nome')
            .eq('numero', conv.numero);
          if (user?.company_id) qn = qn.eq('company_id', user.company_id);
          const { data, error } = await qn.maybeSingle();
          if (!error && data) imageUrl = data.image_url || null;
        } catch (_) {}
      }
      if (!imageUrl) {
        // Fallback: buscar image_url direto da conversa
        try {
          const { data: convRow, error: convErr } = await supabase
            .from('conversas')
            .select('image_url')
            .eq('id', id)
            .maybeSingle();
          if (!convErr && convRow) imageUrl = convRow.image_url || null;
        } catch (_) {}
      }
      if (imageUrl) {
        // Se for Storage privado, assina antes de setar
        const ref = parseStorageRef(imageUrl);
        if (ref) {
          try {
            const signed = await getSignedUrl(ref.bucket, ref.path, 3600);
            setConversations(prev => prev.map(c => c.id === id ? { ...c, avatarUrl: signed || imageUrl } : c));
          } catch (_) {
            setConversations(prev => prev.map(c => c.id === id ? { ...c, avatarUrl: imageUrl } : c));
          }
        } else {
          setConversations(prev => prev.map(c => c.id === id ? { ...c, avatarUrl: imageUrl } : c));
        }
      }
    } catch (_) {}
  };

  const enviarMensagem = async (e) => {
    e.preventDefault();
    const texto = (novaMensagem || '').trim();
    if ((!texto && !pendingImage && !pendingDocument) || !selectedConversationId || !selectedConnectionId) return;
    const conv = conversations.find(c => c.id === selectedConversationId);
    const numero = conv?.numero || conv?.contato_externo_id || null;
    if (!numero) {
      console.warn('[Atendimentos] Número do contato ausente para a conversa selecionada. Bloqueando INSERT.');
      return;
    }
    // Se houver imagem pendente, envia imagem + legenda via send-media
    if (pendingImage) {
      try {
        const now = new Date();
        const tempId = `local-image-${now.getTime()}`;
        setMessages(prev => {
          const arr = prev[selectedConversationId] || [];
          const novo = {
            id: tempId,
            texto: texto || '[imagem]',
            tipoConteudo: pendingImage.mime || 'image/jpeg',
            imageUrl: pendingImage.dataUrl,
            imageMime: pendingImage.mime || 'image/jpeg',
            enviada: true,
            hora: now.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
            tempTs: now.getTime(),
            status: 'sent',
          };
          return { ...prev, [selectedConversationId]: [...arr, novo] };
        });
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 30000);
        const resp = await fetch(API_ENDPOINTS.CONNECTIONS_SEND_MEDIA, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            connection_id: selectedConnectionId,
            conversation_id: selectedConversationId,
            type: 'image',
            file: pendingImage.dataUrl,
            ...(texto ? { text: texto } : {}),
          }),
          signal: controller.signal,
        }).finally(() => clearTimeout(timer));
        if (!resp.ok) {
          const t = await resp.text().catch(() => '');
          console.warn('[Atendimentos] Envio de imagem via backend falhou:', resp.status, t?.slice(0,200));
          setMessages(prev => ({
            ...prev,
            [selectedConversationId]: (prev[selectedConversationId] || []).filter(m => !String(m.id).startsWith('local-image-'))
          }));
          return;
        }
      } catch (err) {
        console.warn('[Atendimentos] Falha ao enviar imagem com legenda:', err?.message || err);
      } finally {
        setPendingImage(null);
        setNovaMensagem('');
      }
      return;
    }
    // Se houver documento pendente, envia documento + legenda via send-media
    if (pendingDocument) {
      try {
        const now = new Date();
        const tempId = `local-document-${now.getTime()}`;
        setMessages(prev => {
          const arr = prev[selectedConversationId] || [];
          const novo = {
            id: tempId,
            texto: texto || `📄 ${pendingDocument.name}`,
            tipoConteudo: pendingDocument.mime || 'application/octet-stream',
            enviada: true,
            hora: now.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
            tempTs: now.getTime(),
            status: 'sent',
          };
          return { ...prev, [selectedConversationId]: [...arr, novo] };
        });
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 30000);
        const resp = await fetch(API_ENDPOINTS.CONNECTIONS_SEND_MEDIA, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            connection_id: selectedConnectionId,
            conversation_id: selectedConversationId,
            type: 'document',
            file: pendingDocument.dataUrl,
            docName: pendingDocument.name,
            ...(texto ? { text: texto } : {}),
          }),
          signal: controller.signal,
        }).finally(() => clearTimeout(timer));
        if (!resp.ok) {
          const t = await resp.text().catch(() => '');
          console.warn('[Atendimentos] Envio de documento via backend falhou:', resp.status, t?.slice(0,200));
          setMessages(prev => ({
            ...prev,
            [selectedConversationId]: (prev[selectedConversationId] || []).filter(m => !String(m.id).startsWith('local-document-'))
          }));
          return;
        }
      } catch (err) {
        console.warn('[Atendimentos] Falha ao enviar documento:', err?.message || err);
      } finally {
        setPendingDocument(null);
        setNovaMensagem('');
      }
      return;
    }
    // Envio somente texto (sem inserir no Supabase para evitar duplicação)
    try {
      const now = new Date();
      const tempId = `local-text-${now.getTime()}`;
      const uiMsg = {
        id: tempId,
        texto,
        tipoConteudo: 'texto',
        enviada: true,
        hora: now.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
        tempTs: now.getTime(),
        status: 'sent',
      };
      setMessages(prev => ({
        ...prev,
        [selectedConversationId]: [...(prev[selectedConversationId] || []), uiMsg]
      }));
      setNovaMensagem('');
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 20000);
      const resp = await fetch(API_ENDPOINTS.CONNECTIONS_SEND_TEXT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          connection_id: selectedConnectionId,
          conversation_id: selectedConversationId,
          text: texto,
        }),
        signal: controller.signal,
      }).finally(() => clearTimeout(timer));
      if (!resp.ok) {
        const txt = await resp.text().catch(() => '');
        console.warn('[Atendimentos] Envio via backend falhou:', resp.status, txt?.slice(0, 200));
        // Remover mensagem otimista caso falhe
        setMessages(prev => ({
          ...prev,
          [selectedConversationId]: (prev[selectedConversationId] || []).filter(m => m.id !== tempId)
        }));
      }
    } catch (we) {
      console.warn('[Atendimentos] Falha ao enviar via backend/Uazapi:', we?.message || we);
    }
  };

const getDisplayName = (item) => {
    if (!item) return 'Sem nome';
    return (
      item.nome ||
      item.name ||
      item.contato_nome ||
      item.contato ||
      item.numero ||
      item.phone ||
      'Sem nome'
    );
  };

  const getIniciais = (nome) => {
    if (!nome || typeof nome !== 'string') return '??';
    const cleaned = nome.trim();
    if (!cleaned) return '??';
    // se for número de telefone, tenta pegar os últimos dígitos
    if (/^\+?\d[\d\s-()]+$/.test(cleaned)) {
      const digits = cleaned.replace(/\D/g, '');
      return (digits.slice(-2) || '??').toUpperCase();
    }
    const parts = cleaned.split(' ').filter(Boolean);
    const initials = parts.map(p => p[0]).join('').toUpperCase();
    return (initials || '??').substring(0, 2);
  };

  const getNomeConversaSelecionada = () => {
    const conv = conversations.find(c => c.id === selectedConversationId);
    return conv ? getDisplayName(conv) : '';
  };

  // Formata número de telefone para exibição (remove 55 e formata)
  const formatPhoneForDisplay = (phone) => {
    if (!phone) return '';
    // Remove tudo que não é dígito
    let digits = String(phone).replace(/\D/g, '');
    // Remove código do Brasil (55) se presente no início
    if (digits.startsWith('55') && digits.length > 10) {
      digits = digits.substring(2);
    }
    // Formata: (XX) XXXXX-XXXX ou (XX) XXXX-XXXX
    if (digits.length === 11) {
      return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
    } else if (digits.length === 10) {
      return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
    }
    return digits;
  };

  const getNumeroConversaSelecionada = () => {
    const conv = conversations.find(c => c.id === selectedConversationId);
    return conv?.numero ? formatPhoneForDisplay(conv.numero) : '';
  };

  const getAvatarConversaSelecionada = () => {
    const conv = conversations.find(c => c.id === selectedConversationId);
    return conv?.avatarUrl || conv?.rawImageUrl || null;
  };

  // Monta src para avatar com cache-buster (evita cache de CDN/HTTP)
  const buildAvatarSrc = (url) => {
    try {
      if (!url) return null;
      // Corrige URLs externas malformadas (ex.: "https:/pps.whatsapp.net" ➜ "https://pps.whatsapp.net")
      if (/^https:\/pps\./i.test(url)) {
        url = url.replace(/^https:\/pps\./i, 'https://pps.');
      }
      if (/^http:\/\//i.test(url)) {
        // preferir https
        url = url.replace(/^http:\/\//i, 'https://');
      }
      if (/^data:/.test(url)) return url;
      // Não adicionar cache-buster em URLs já assinadas (Supabase signed URLs) para não invalidar assinatura
      if (/\/storage\/v1\/object\/sign\//.test(url) || /[?&]token=/.test(url)) {
        return url;
      }
      const sep = url.includes('?') ? '&' : '?';
      // muda a cada minuto
      return `${url}${sep}v=${Math.floor(Date.now() / 60000)}`;
    } catch (_) {
      return url;
    }
  };

  // Detecta URL pública do Supabase Storage (não precisa assinar)
  const isPublicStorageUrl = (url) => {
    try {
      if (!url || typeof url !== 'string') return false;
      return /\/storage\/v1\/object\/public\//.test(url);
    } catch (_) { return false; }
  };

  // Normaliza possíveis URLs de avatar vindas do banco (corrige protocolo quebrado e força https)
  const normalizeAvatarUrl = (url) => {
    try {
      if (!url || typeof url !== 'string') return null;
      let out = url.trim();
      // Corrige casos como "https:/pps.whatsapp.net" (1 barra só)
      if (/^https:\/pps\./i.test(out)) {
        out = out.replace(/^https:\/pps\./i, 'https://pps.');
      }
      if (/^http:\/pps\./i.test(out)) {
        out = out.replace(/^http:\/pps\./i, 'https://pps.');
      }
      // Força https quando protocolo http
      if (/^http:\/\//i.test(out)) {
        out = out.replace(/^http:\/\//i, 'https://');
      }
      return out;
    } catch (_) {
      return url || null;
    }
  };

  // Fallback quando a imagem do avatar falha: remove o avatar para cair nas iniciais
  const handleAvatarError = (convId) => {
    setConversations(prev => prev.map(c => c.id === convId ? { ...c, avatarUrl: null, rawImageUrl: null } : c));
  };

  const handleSelectedAvatarError = () => {
    if (!selectedConversationId) return;
    setConversations(prev => prev.map(c => c.id === selectedConversationId ? { ...c, avatarUrl: null, rawImageUrl: null } : c));
  };

  const conversasFiltradas = conversations.filter(c => {
    const nome = (getDisplayName(c) || '').toLowerCase();
    const ultima = (c.ultimaMensagem || c.last_message || c.message || '').toLowerCase();
    const q = busca.toLowerCase();
    return nome.includes(q) || ultima.includes(q);
  });

  const messagesEndRef = useRef(null);
  const openedConversationsRef = useRef(new Set());
  const mensagensContainerRef = useRef(null);
  const [showScrollDown, setShowScrollDown] = useState(false);

  // Auto-scroll quando uma conversa é aberta e suas mensagens são carregadas
  useEffect(() => {
    if (!selectedConversationId) return;
    const conversationMessages = messages[selectedConversationId];
    // Só rola se a conversa tem mensagens carregadas
    if (conversationMessages && conversationMessages.length > 0) {
      // Usa timeout maior para garantir que o DOM foi atualizado
      const timer = setTimeout(() => {
        scrollToBottom(false); // false = sem animação suave para ser instantâneo
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [selectedConversationId, messages[selectedConversationId]?.length]);

  const scrollToBottom = (smooth = true) => {
    try {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto' });
      } else if (mensagensContainerRef.current) {
        const el = mensagensContainerRef.current;
        el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
      }
    } catch (_) {}
  };

  const handleMensagensScroll = () => {
    try {
      const el = mensagensContainerRef.current;
      if (!el) return;
      const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
      setShowScrollDown(!nearBottom);
    } catch (_) {}
  };

  useEffect(() => {
    try {
      handleMensagensScroll();
      // Auto-scroll quando novas mensagens chegam, se usuário estiver perto do final
      const el = mensagensContainerRef.current;
      if (el && selectedConversationId) {
        const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 200;
        if (nearBottom) {
          setTimeout(() => scrollToBottom(true), 50);
        }
      }
    } catch (_) {}
  }, [selectedConversationId, messages]);

  // Esc para fechar lightbox
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') setIsLightboxOpen(false);
    };
    if (isLightboxOpen) {
      window.addEventListener('keydown', onKey);
    }
    return () => window.removeEventListener('keydown', onKey);
  }, [isLightboxOpen]);

  const openLightbox = (src, mime) => {
    setLightboxSrc(src || '');
    setLightboxType(mime || 'image/jpeg');
    setIsLightboxOpen(true);
  };
  const closeLightbox = () => setIsLightboxOpen(false);

  // ============================================================================
  // FUNÇÕES DA SIDEBAR DE AÇÕES
  // ============================================================================

  const handleDeleteConversation = async () => {
    if (!selectedConversationId) return;
    
    const confirmDelete = window.confirm('Tem certeza que deseja excluir esta conversa? Esta ação não pode ser desfeita.');
    if (!confirmDelete) return;

    try {
      // Deletar conversa do banco
      const { error } = await supabase
        .from('conversas')
        .delete()
        .eq('id', selectedConversationId);

      if (error) throw error;

      // Remover do estado local
      setConversations(prev => prev.filter(c => c.id !== selectedConversationId));
      setMessages(prev => {
        const newMessages = { ...prev };
        delete newMessages[selectedConversationId];
        return newMessages;
      });
      
      // Limpar seleção
      setSelectedConversationId(null);
      
      
    } catch (error) {
      console.error('[Atendimentos] Erro ao excluir conversa:', error);
      alert('Erro ao excluir conversa. Tente novamente.');
    }
  };

  const handleToggleFavorite = async () => {
    if (!selectedConversationId) return;

    try {
      const conv = conversations.find(c => c.id === selectedConversationId);
      const newFavoritoValue = !conv?.favorito;

      // Atualizar no banco
      const { error } = await supabase
        .from('conversas')
        .update({ favorito: newFavoritoValue })
        .eq('id', selectedConversationId);

      if (error) throw error;

      // Atualizar estado local
      setConversations(prev => 
        prev.map(c => 
          c.id === selectedConversationId 
            ? { ...c, favorito: newFavoritoValue }
            : c
        )
      );

      
    } catch (error) {
      console.error('[Atendimentos] Erro ao atualizar favorito:', error);
      alert('Erro ao atualizar favorito. Tente novamente.');
    }
  };

  const handleExportPDF = async () => {
    if (!selectedConversationId) return;

    const msgs = messages[selectedConversationId] || [];
    
    if (msgs.length === 0) {
      alert('Não há mensagens para exportar.');
      return;
    }

    try {
      // Importação dinâmica do jsPDF
      const { default: jsPDF } = await import('jspdf');
      
      const doc = new jsPDF();
      const conv = conversations.find(c => c.id === selectedConversationId);
      const nomeContato = conv?.nome_contato || 'Conversa';
      
      // Título
      doc.setFontSize(16);
      doc.text(`Conversa com ${nomeContato}`, 20, 20);
      
      // Data de exportação
      doc.setFontSize(10);
      doc.text(`Exportado em: ${new Date().toLocaleString('pt-BR')}`, 20, 30);
      
      // Mensagens
      doc.setFontSize(12);
      let y = 45;
      const pageHeight = doc.internal.pageSize.height;
      const margin = 20;
      
      msgs.forEach((msg, index) => {
        const texto = msg.texto || msg.conteudo || '[sem texto]';
        const remetente = msg.enviada ? 'Você' : nomeContato;
        const hora = msg.hora || new Date(msg.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
        
        const linha = `[${hora}] ${remetente}: ${texto}`;
        
        // Quebra de linha se necessário
        const lines = doc.splitTextToSize(linha, 170);
        
        // Nova página se não couber
        if (y + (lines.length * 7) > pageHeight - margin) {
          doc.addPage();
          y = 20;
        }
        
        doc.text(lines, 20, y);
        y += lines.length * 7 + 3;
      });
      
      // Salvar PDF
      const fileName = `conversa_${nomeContato.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);
      
      
    } catch (error) {
      console.error('[Atendimentos] Erro ao exportar PDF:', error);
      alert('Erro ao exportar PDF. Verifique se a biblioteca jsPDF está instalada.');
    }
  };

  // Handlers do redimensionamento horizontal
  const onResizeMouseDown = (e) => {
    e.preventDefault();
    setIsResizing(true);
  };
  useEffect(() => {
    if (!isResizing) return;
    const onMove = (e) => {
      const el = containerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const x = e.clientX - rect.left;
      let pct = (x / rect.width) * 100;
      // Limites entre 20% e 60%
      pct = Math.max(20, Math.min(60, pct));
      setSidebarWidthPct(pct);
    };
    const onUp = () => {
      setIsResizing(false);
      // Persistir preferência do usuário
      try { localStorage.setItem('atendimentos_sidebar_width_pct', String(sidebarWidthPct)); } catch (_) {}
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    // Evita seleção de texto durante o arraste
    const prevUserSelect = document.body.style.userSelect;
    document.body.style.userSelect = 'none';
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.userSelect = prevUserSelect;
    };
  }, [isResizing, sidebarWidthPct]);

  // Helper: normaliza base64 e retorna Data URL segura para qualquer mídia
  const buildMediaDataUrl = (base64, mime = 'application/octet-stream') => {
    try {
      if (!base64) return '';
      let b64 = String(base64);
      // Se já vier em data URL, extrai somente a parte base64
      if (b64.startsWith('data:')) {
        const comma = b64.indexOf(',');
        if (comma !== -1) b64 = b64.slice(comma + 1);
      }
      // Remove caracteres que não pertencem a base64 e espaços/quebras de linha
      b64 = b64.replace(/[^A-Za-z0-9+/=]/g, '');
      return `data:${mime};base64,${b64}`;
    } catch (_) {
      return '';
    }
  };

  if (initialLoading) {
    return (
      <div className="atendimentos-container">
        <LoadingSpinner message="Carregando atendimentos..." />
      </div>
    );
  }

  return (
    <div className="atendimentos-container">
      {/* Cabeçalho no estilo SettingsPage: abas com as conexões conectadas */}
      <div className="settings-tabs">
        {orderedConnections.length === 0 && (
          <button className="tab-button" disabled>Nenhuma conexão conectada</button>
        )}
        {orderedConnections.map((conn, idx) => (
          <div
            key={conn.id}
            draggable
            onDragStart={(e) => onTabDragStart(e, idx)}
            onDragOver={onTabDragOver}
            onDrop={(e) => onTabDrop(e, idx)}
            style={{ display: 'inline-flex' }}
          >
            <button
              className={`tab-button ${selectedConnectionId === conn.id ? 'active' : ''}`}
              onClick={() => setSelectedConnectionId(conn.id)}
              title={conn.number || ''}
              type="button"
            >
              {conn.name}
            </button>
          </div>
        ))}
      </div>

      {/* Wrapper principal que contém lista de conversas e área de mensagens */}
      <div className="conteudo-principal" ref={containerRef} style={{ display: 'flex', width: '100%', minHeight: 0 }}>
        {/* Lista de conversas */}
        <div
          className={`conversas-lista ${selectedConversationId ? 'conversa-selecionada' : ''}`}
          style={{ width: `${sidebarWidthPct}%`, minWidth: '180px' }}
        >
          <div className="conversas-header">
            <h3>Conversas</h3>
          </div>

          <div className="search-bar">
            <input
              type="text"
              placeholder="Buscar contato"
              value={busca}
              onChange={e => setBusca(e.target.value)}
            />
            {busca.trim().length >= 2 && (
              <div style={{ position: 'relative' }}>
                <div
                  className="search-results"
                  style={{
                    position: 'absolute',
                    top: 8,
                    left: 0,
                    right: 0,
                    background: 'var(--card-background)',
                    border: '1px solid var(--secondary-color)',
                    borderRadius: 8,
                    padding: '6px 0',
                    zIndex: 5,
                    maxHeight: 260,
                    overflowY: 'auto',
                  }}
                >
                  {isSearching && (
                    <div style={{ padding: '8px 12px', color: 'var(--text-color-secondary)' }}>Buscando...</div>
                  )}
                  {!isSearching && searchResults.length === 0 && (
                    <div style={{ padding: '8px 12px', color: 'var(--text-color-secondary)' }}>Nenhum contato encontrado</div>
                  )}
                  {searchResults.map((c) => (
                    <div
                      key={c.id}
                      className="search-result-item"
                      onClick={() => handleSelectContact(c)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '8px 12px', cursor: 'pointer'
                      }}
                    >
                      <div className="conversa-avatar" style={{ width: 28, height: 28, marginRight: 0 }}>
                        {c.image_url ? (
                          <img src={c.image_url} alt={c.nome || c.numero || 'Contato'} style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover' }} />
                        ) : (
                          (c.nome || 'C').slice(0,1).toUpperCase()
                        )}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span style={{ color: 'var(--text-color)', fontSize: 14, lineHeight: '18px' }}>{c.nome || 'Contato'}</span>
                        <span style={{ color: 'var(--text-color-secondary)', fontSize: 12 }}>{c.numero || ''}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div 
            className="conversas" 
            ref={conversationsListRef}
            onScroll={handleConversationsScroll}
          >
            {conversasFiltradas.map(conv => (
              <div
                key={conv.id}
                className={`conversa-item ${selectedConversationId === conv.id ? 'active' : ''}`}
                onClick={() => selecionarConversa(conv.id)}
              >
                <div className="conversa-avatar">
                  {(conv.avatarUrl || conv.rawImageUrl) ? (
                    <img
                      src={buildAvatarSrc(conv.avatarUrl || conv.rawImageUrl)}
                      alt={getDisplayName(conv)}
                      style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', cursor: 'zoom-in' }}
                      onError={() => handleAvatarError(conv.id)}
                      onClick={() => openLightbox(buildAvatarSrc(conv.avatarUrl || conv.rawImageUrl), 'image/jpeg')}
                    />
                  ) : (
                    getIniciais(getDisplayName(conv))
                  )}
                </div>
                <div className="conversa-info">
                  <div className="conversa-header">
                    <div className="conversa-nome">{getDisplayName(conv)}</div>
                    <div className="conversa-hora">{conv.hora}</div>
                  </div>
                  <div className="conversa-preview">
                    {conv.ultimaMensagem || conv.last_message || conv.message}
                  </div>
                </div>
              </div>
            ))}
            {/* Indicador de carregamento de mais conversas */}
            {loadingMoreConversations && (
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                padding: '12px',
                color: 'var(--text-color-secondary)'
              }}>
                <AppIcon name="progress_activity" style={{ animation: 'spin 1s linear infinite' }} />
              </div>
            )}
            {/* Indicador de fim da lista */}
            {!hasMoreConversations && conversations.length > 0 && (
              <div style={{ 
                textAlign: 'center', 
                padding: '12px',
                color: 'var(--text-color-secondary)',
                fontSize: '12px'
              }}>
                Todas as conversas carregadas
              </div>
            )}
          </div>
        </div>

        {/* Divisor arrastável */}
        <div
          className="resizer-col"
          onMouseDown={onResizeMouseDown}
          title="Arraste para redimensionar"
          style={{
            width: '6px',
            cursor: 'col-resize',
            background: isResizing ? 'rgba(0,0,0,0.2)' : 'transparent',
            transition: 'background 0.15s ease',
          }}
        />

        {/* Área de mensagens */}
        <div className="mensagens-area" style={{ flex: 1, minWidth: 0, position: 'relative' }}>
          {selectedConversationId ? (
            <>
              {/* Sidebar de ações */}
              <ChatActionsSidebar
                conversationId={selectedConversationId}
                isFavorited={conversations.find(c => c.id === selectedConversationId)?.favorito || false}
                onEditContact={handleEditContact}
                onDelete={handleDeleteConversation}
                onToggleFavorite={handleToggleFavorite}
                onExportPDF={handleExportPDF}
              />

              <div className="chat-header">
                <div className="chat-avatar">
                  {getAvatarConversaSelecionada() ? (
                    <img
                      src={buildAvatarSrc(getAvatarConversaSelecionada())}
                      alt={getNomeConversaSelecionada()}
                      style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', cursor: 'zoom-in' }}
                      onError={handleSelectedAvatarError}
                      onClick={() => openLightbox(buildAvatarSrc(getAvatarConversaSelecionada()), 'image/jpeg')}
                    />
                  ) : (
                    getIniciais(getNomeConversaSelecionada())
                  )}
                </div>
                <div className="chat-info">
                  <h3 className="chat-nome">{getNomeConversaSelecionada()}</h3>
                  <div className="chat-status">{getNumeroConversaSelecionada() || 'Contato'}</div>
                </div>
                {/* Botão de menu (3 pontos) alinhado à direita */}
                <div className="chat-menu" ref={chatMenuRef}>
                  <button
                    type="button"
                    className="chat-menu-button"
                    onClick={(e) => { e.stopPropagation(); setShowChatMenu((v) => !v); }}
                    title="Opções"
                    aria-haspopup="menu"
                    aria-expanded={showChatMenu ? 'true' : 'false'}
                  >
                    <AppIcon name="more_vert" />
                  </button>
                  {showChatMenu && (
                    <div className="chat-menu-dropdown" role="menu">
                      <div
                        className="chat-menu-item"
                        role="menuitem"
                        onClick={() => {
                          setShowChatMenu(false);
                          alert('Arquivar conversa (em breve)');
                        }}
                      >
                        Arquivar conversa
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="mensagens" ref={mensagensContainerRef} onScroll={handleMensagensScroll} style={{ position: 'relative' }}>
                {messages[selectedConversationId] && messages[selectedConversationId].map((msg, idx, arr) => {
                  // Inserir separador de dia quando virar o dia
                  const curDate = getMsgDate(msg);
                  const showDaySeparator = (() => {
                    if (idx === 0) return true;
                    const prev = arr[idx - 1];
                    const prevDate = getMsgDate(prev);
                    return !sameDay(curDate, prevDate);
                  })();
                  const tipo = msg.tipoConteudo || msg.tipo_conteudo || 'texto';
                  const tipoLower = (typeof tipo === 'string' ? tipo.toLowerCase() : 'texto');
                  // Detecta primeiro áudio para evitar que uma URL acidental faça cair no bloco de imagem
                  const isAudio = (tipoLower === 'audio' || tipoLower.startsWith('audio/') || tipoLower === 'ptt' || tipoLower === 'myaudio') || !!msg.audioUrl;
                  const isVideo = (!isAudio) && ((tipoLower.startsWith('video')) || !!msg.videoUrl || !!msg.videoPoster);
                  const isImage = (!isAudio && !isVideo) && ((tipoLower.startsWith('image')) || !!msg.imageUrl);
                  // Detecta documento por tipo ou por extensão no caminho_storage
                  const hasDocumentExtension = msg.caminho_storage && /\.(pdf|doc|docx|xls|xlsx|ppt|pptx|txt|zip|rar|bin)$/i.test(msg.caminho_storage);
                  const isDocument = (!isAudio && !isVideo && !isImage) && (tipoLower.startsWith('application/') || tipo === 'document' || hasDocumentExtension);
                  const isJsonText = (typeof msg.texto === 'string' && msg.texto.trim().startsWith('{') && msg.texto.trim().endsWith('}'));
                  const payload = isJsonText ? (() => { try { return JSON.parse(msg.texto); } catch (_) { return null; } })() : null;
                  const buttonPayload = (() => {
                    try {
                      const parsed = msg?.metadados?.parsed_button_payload;
                      if (!parsed || typeof parsed !== 'object') return null;
                      const choices = Array.isArray(parsed.choices)
                        ? parsed.choices.map((c) => String(c || '').trim()).filter(Boolean)
                        : [];
                      if (choices.length === 0) return null;
                      return {
                        text: String(parsed.text || msg.texto || '').trim() || null,
                        choices,
                        selectedText: String(parsed.selected_text || '').trim() || null
                      };
                    } catch (_) {
                      return null;
                    }
                  })();
                  const isButtonMessage = !!buttonPayload;
                  const isContact = (tipoLower === 'contato') || (payload && payload.kind === 'contact');
                  return (
                    <React.Fragment key={msg.id}>
                      {showDaySeparator && (
                        <div className="day-separator"><span className="label">{formatDayLabel(curDate)}</span></div>
                      )}
                      <div className={msg.enviada ? 'mensagem-enviada' : 'mensagem-recebida'}>
                      <div className="mensagem-conteudo">
                        {isImage ? (
                          (() => {
                            const rawText = (msg.texto ?? msg.conteudo ?? '');
                            const json = parseConteudoJson(rawText);
                            const thumb = json?.JPEGThumbnail ? `data:image/jpeg;base64,${json.JPEGThumbnail}` : null;
                            const hasFull = !!msg.imageUrl;
                            const showSrc = hasFull ? msg.imageUrl : thumb;
                            const caption = (() => {
                              try {
                                const raw = String(rawText || '').trim();
                                if (!raw) return null;
                                const telegramMeta = msg?.metadados?.telegram || {};
                                const imageDescription = String(telegramMeta.image_description || '').trim();
                                if (imageDescription && raw === imageDescription) return null;
                                const isJson = raw.startsWith('{') && raw.endsWith('}');
                                if (isJson) {
                                  // alguns providers mandam caption no JSON
                                  if (json && typeof json.caption === 'string' && json.caption.trim()) return json.caption.trim();
                                  return null;
                                }
                                // Evitar mostrar placeholder local
                                if (/^\[imagem\]/i.test(raw)) return null;
                                return raw;
                              } catch (_) { return null; }
                            })();
                            if (showSrc) {
                              return (
                                <>
                                  <div style={{ position: 'relative', display: 'inline-block' }}>
                                    <img
                                      src={showSrc}
                                      alt="imagem"
                                      style={{ maxWidth: '720px', width: '100%', height: 'auto', borderRadius: '8px', cursor: hasFull ? 'zoom-in' : 'default', opacity: downloadingMediaIds.has(msg.id) ? 0.8 : 1 }}
                                      onClick={() => hasFull && openLightbox(showSrc, msg.imageMime || tipo)}
                                    />
                                    {!hasFull && (
                                      <button
                                        type="button"
                                        className="botao-enviar"
                                        onClick={() => requestFullMediaDownload(msg.id)}
                                        style={{ position: 'absolute', right: 10, bottom: 10 }}
                                        disabled={downloadingMediaIds.has(msg.id)}
                                        title={downloadingMediaIds.has(msg.id) ? 'Baixando imagem...' : 'Baixar em alta qualidade'}
                                      >
                                        {downloadingMediaIds.has(msg.id) ? 'Baixando...' : 'Baixar HQ'}
                                      </button>
                                    )}
                                  </div>
                                  {caption && (
                                    <div className="mensagem-caption" style={{ marginTop: 6, fontSize: 14 }}>
                                      {caption}
                                    </div>
                                  )}
                                </>
                              );
                            }
                            return (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                <span style={{ fontSize: 12, opacity: 0.8 }}>Imagem protegida. Aguardando processamento...</span>
                                <div style={{ display: 'flex', gap: 8 }}>
                                  <button onClick={() => refreshMessageMedia(msg.id)} className="botao-enviar" type="button" style={{ alignSelf: 'flex-start' }}>
                                    Tentar carregar agora
                                  </button>
                                  <button onClick={() => requestFullMediaDownload(msg.id)} className="botao-enviar" type="button" style={{ alignSelf: 'flex-start' }}>
                                    Baixar HQ
                                  </button>
                                </div>
                              </div>
                            );
                          })()
                        ) : isVideo ? (
                          (() => {
                            const hasFull = !!msg.videoUrl;
                            const poster = msg.videoPoster || null;
                            if (hasFull) {
                              // Mostrar apenas o poster com ícone de play; abrir player ao clicar
                              const posterSrc = poster || undefined;
                              return (
                                <div style={{ position: 'relative', display: 'inline-block' }}>
                                  {posterSrc ? (
                                    <img
                                      src={posterSrc}
                                      alt="vídeo"
                                      style={{ maxWidth: '720px', width: '100%', height: 'auto', borderRadius: '8px', cursor: 'pointer' }}
                                      onClick={() => openLightbox(msg.videoUrl, msg.videoMime || 'video/mp4')}
                                    />
                                  ) : (
                                    <div style={{ width: '100%', maxWidth: 720, aspectRatio: '16/9', background: '#000', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}
                                         onClick={() => openLightbox(msg.videoUrl, msg.videoMime || 'video/mp4')}>
                                      Clique para reproduzir o vídeo
                                    </div>
                                  )}
                                  <button
                                    type="button"
                                    className="botao-enviar"
                                    style={{ position: 'absolute', left: 10, bottom: 10 }}
                                    onClick={() => openLightbox(msg.videoUrl, msg.videoMime || 'video/mp4')}
                                  >
                                    ▶ Reproduzir
                                  </button>
                                </div>
                              );
                            }
                            // Sem vídeo em HQ ainda: mostra poster (se houver) e botão Baixar HQ
                            if (poster) {
                              return (
                                <div style={{ position: 'relative', display: 'inline-block' }}>
                                  <img
                                    src={poster}
                                    alt="vídeo (preview)"
                                    style={{ maxWidth: '720px', width: '100%', height: 'auto', borderRadius: '8px', opacity: downloadingMediaIds.has(msg.id) ? 0.8 : 1 }}
                                  />
                                  <button
                                    type="button"
                                    className="botao-enviar"
                                    onClick={() => requestFullMediaDownload(msg.id)}
                                    style={{ position: 'absolute', right: 10, bottom: 10 }}
                                    disabled={downloadingMediaIds.has(msg.id)}
                                    title={downloadingMediaIds.has(msg.id) ? 'Baixando vídeo...' : 'Baixar em alta qualidade'}
                                  >
                                    {downloadingMediaIds.has(msg.id) ? 'Baixando...' : 'Baixar HQ'}
                                  </button>
                                </div>
                              );
                            }
                            // Último recurso
                            return (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                <span style={{ fontSize: 12, opacity: 0.8 }}>Vídeo protegido. Aguardando processamento...</span>
                                <div style={{ display: 'flex', gap: 8 }}>
                                  <button onClick={() => refreshMessageMedia(msg.id)} className="botao-enviar" type="button" style={{ alignSelf: 'flex-start' }}>
                                    Tentar carregar agora
                                  </button>
                                  <button onClick={() => requestFullMediaDownload(msg.id)} className="botao-enviar" type="button" style={{ alignSelf: 'flex-start' }}>
                                    Baixar HQ
                                  </button>
                                </div>
                              </div>
                            );
                          })()
                        ) : isAudio ? (
                          msg.audioUrl ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                              <audio controls preload="metadata" src={msg.audioUrl} />
                              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <button
                                  type="button"
                                  onClick={() => handleTranscribeClick(msg)}
                                  title={(String(msg.transcricao || msg?.metadados?.telegram?.transcription || msg.texto || '').trim()) ? (showTranscriptionIds.has(msg.id) ? 'Ocultar transcrição' : 'Mostrar transcrição') : 'Sem transcrição disponível'}
                                  aria-label="Transcrição"
                                  style={{ background: 'transparent', border: 'none', color: 'var(--text-color)', cursor: 'pointer', padding: 4 }}
                                >
                                  <AppIcon name="subject" style={{ fontSize: 18 }} />
                                </button>
                              </div>
                              {(() => {
                                const transcription = String(msg.transcricao || msg?.metadados?.telegram?.transcription || msg.texto || '').trim();
                                if (!showTranscriptionIds.has(msg.id) || !transcription) return null;
                                return (
                                <div style={{ fontSize: 14, lineHeight: '20px', color: 'var(--text-color)' }}>
                                  <strong>Transcrição:</strong> {transcription}
                                </div>
                                );
                              })()}
                            </div>
                          ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                              <span style={{ fontSize: 12, opacity: 0.8 }}>Mídia de áudio protegida. Aguardando processamento...</span>
                              <button onClick={() => refreshMessageMedia(msg.id)} className="botao-enviar" type="button" style={{ alignSelf: 'flex-start' }}>
                                Tentar carregar agora
                              </button>
                            </div>
                          )
                        ) : isDocument ? (
                          (() => {
                            // Extrair nome do documento do texto ou caminho_storage
                            const rawText = (msg.texto ?? msg.conteudo ?? '');
                            let docName = 'Documento';
                            
                            // Tentar extrair do texto (formato: "📄 nome.pdf")
                            if (rawText.startsWith('📄 ')) {
                              docName = rawText.replace('📄 ', '').trim();
                            } else if (msg.caminho_storage) {
                              // Extrair do caminho: "media:empresa/conversa/timestamp.pdf"
                              const parts = msg.caminho_storage.split('/');
                              docName = parts[parts.length - 1] || 'Documento';
                            }
                            
                            // Extrair extensão
                            const ext = docName.split('.').pop()?.toUpperCase() || 'FILE';
                            
                            // Função para baixar documento
                            const handleDownload = async () => {
                              if (!msg.caminho_storage) return;
                              
                              try {
                                const [bucket, ...pathParts] = msg.caminho_storage.split(':');
                                const path = pathParts.join(':');
                                
                                const response = await fetch(API_ENDPOINTS.MEDIA_SIGNED_URL, {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ 
                                    caminho_storage: msg.caminho_storage,
                                    empresa_id: companyId 
                                  })
                                });
                                
                                if (!response.ok) {
                                  alert('Erro ao gerar link de download');
                                  return;
                                }
                                
                                const data = await response.json();
                                if (data.signedUrl) {
                                  window.open(data.signedUrl, '_blank');
                                }
                              } catch (err) {
                                console.error('Erro ao baixar documento:', err);
                                alert('Erro ao baixar documento');
                              }
                            };
                            
                            // Verificar se há texto adicional (não é apenas o nome do documento)
                            const hasAdditionalText = rawText && !rawText.startsWith('📄 ');
                            
                            return (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <div 
                                  onClick={msg.caminho_storage ? handleDownload : undefined}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    padding: '12px',
                                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                    borderRadius: '8px',
                                    border: '1px solid rgba(168, 192, 217, 0.2)',
                                    cursor: msg.caminho_storage ? 'pointer' : 'default',
                                    transition: 'all 0.2s',
                                  }}
                                  onMouseEnter={(e) => {
                                    if (msg.caminho_storage) {
                                      e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.08)';
                                      e.currentTarget.style.borderColor = 'rgba(168, 192, 217, 0.4)';
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                                    e.currentTarget.style.borderColor = 'rgba(168, 192, 217, 0.2)';
                                  }}
                                  title={msg.caminho_storage ? 'Clique para baixar' : undefined}
                                >
                                  <div style={{
                                    width: '48px',
                                    height: '48px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: '6px',
                                    backgroundColor: 'rgba(78, 84, 200, 0.2)',
                                    flexShrink: 0,
                                  }}>
                                    <AppIcon name="description" style={{ fontSize: '28px', color: '#4e54c8' }} />
                                  </div>
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ 
                                      fontWeight: 500, 
                                      fontSize: '14px',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}>
                                      {docName}
                                    </div>
                                    <div style={{ 
                                      fontSize: '12px', 
                                      opacity: 0.7,
                                      marginTop: '2px',
                                    }}>
                                      {ext} {msg.caminho_storage && '• Clique para baixar'}
                                    </div>
                                  </div>
                                  {msg.caminho_storage && (
                                    <AppIcon name="download" style={{ fontSize: '24px', color: '#4e54c8', flexShrink: 0 }} />
                                  )}
                                </div>
                                {hasAdditionalText && (
                                  <div style={{ fontSize: '14px', lineHeight: '1.4' }}>
                                    {rawText}
                                  </div>
                                )}
                              </div>
                            );
                          })()
                        ) : (
                          isButtonMessage ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 220 }}>
                              {buttonPayload.text && (
                                <div style={{ fontSize: 14, lineHeight: '1.45' }}>{buttonPayload.text}</div>
                              )}
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                {buttonPayload.choices.map((choice, cIdx) => (
                                  <div
                                    key={`${msg.id}-btn-choice-${cIdx}`}
                                    style={{
                                      border: '1px solid rgba(255,255,255,0.28)',
                                      borderRadius: 10,
                                      padding: '7px 10px',
                                      fontSize: 13.5,
                                      background: 'rgba(255,255,255,0.08)'
                                    }}
                                  >
                                    {choice}
                                  </div>
                                ))}
                              </div>
                              {buttonPayload.selectedText && (
                                <div style={{ fontSize: 12, opacity: 0.78 }}>
                                  Selecionado: {buttonPayload.selectedText}
                                </div>
                              )}
                            </div>
                          ) : isContact ? (
                            (() => {
                              const allPhones = Array.isArray(payload?.phones) ? payload.phones : [];
                              const plusPhones = allPhones.map(p => String(p||'').trim()).filter(p => p.startsWith('+'));
                              const displayPhoneRaw = plusPhones[0] || null;
                              const displayPhone = displayPhoneRaw ? String(displayPhoneRaw).replace(/^\+/, '') : null;
                              const avatar = payload?.avatar || null;
                              const initials = String(payload?.name || 'C').slice(0,1).toUpperCase();
                              return (
                                <div style={{
                                  display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 12,
                                  padding: 16, border: '1px solid var(--secondary-color)', borderRadius: 12,
                                  background: 'var(--card-background)', maxWidth: 320, margin: '0 auto'
                                }}>
                                  {avatar ? (
                                    <img
                                      src={avatar}
                                      alt="avatar do contato"
                                      style={{ width: 120, height: 120, borderRadius: '50%', objectFit: 'cover' }}
                                    />
                                  ) : (
                                    <div style={{
                                      width: 120, height: 120, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                      background: 'linear-gradient(180deg, rgba(20,24,38,0.92) 0%, rgba(20,24,38,0.8) 100%)',
                                      color: '#a8c0d9', fontSize: 48, fontWeight: 700
                                    }}>
                                      {initials}
                                    </div>
                                  )}
                                  <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-color)' }}>
                                    {payload?.name || 'Contato'}
                                  </div>
                                  {displayPhone && (
                                    <div style={{ color: 'var(--text-color-secondary)', fontSize: 14 }}>
                                      +{displayPhone}
                                    </div>
                                  )}
                                  <button
                                    type="button"
                                    className="botao-enviar"
                                    onClick={() => handleSaveAndChat(payload)}
                                    style={{ width: '100%', padding: '10px 14px', borderRadius: 9999 }}
                                  >
                                    Salvar e Conversar
                                  </button>
                                </div>
                              );
                            })()
                          ) : (
                            <>{isJsonText ? '[payload recebido]' : msg.texto}</>
                          )
                        )}
                        <div className="mensagem-hora" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span>{msg.hora}</span>
                          {msg.enviada && (
                            msg.status === 'sending' ? (
                              <AppIcon name="schedule" />
                            ) : (
                              <AppIcon name="done_all" />
                            )
                          )}
                        </div>
                      </div>
                    </div>
                    </React.Fragment>
                  );
                })}
                {/* elemento para rolar ao final */}
                <div ref={messagesEndRef} />
                {showScrollDown && (
                  <div style={{ position: 'sticky', bottom: 14, zIndex: 5, display: 'flex', justifyContent: 'flex-end', pointerEvents: 'none' }}>
                    <button
                      type="button"
                      onClick={() => scrollToBottom(true)}
                      title="Ir para a última mensagem"
                      aria-label="Ir para a última mensagem"
                      style={{
                        pointerEvents: 'auto',
                        marginRight: 14,
                        width: 44,
                        height: 44,
                        borderRadius: 9999,
                        border: '1px solid var(--secondary-color)',
                        background: 'linear-gradient(180deg, rgba(20,24,38,0.92) 0%, rgba(20,24,38,0.8) 100%)',
                        color: '#a8c0d9',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        boxShadow: '0 8px 20px rgba(0,0,0,0.35)',
                        backdropFilter: 'blur(4px)',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 10px 24px rgba(0,0,0,0.45)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.35)'; }}
                      onFocus={(e) => { e.currentTarget.style.outline = 'none'; e.currentTarget.style.boxShadow = '0 0 0 2px rgba(78,84,200,0.35), 0 10px 24px rgba(0,0,0,0.45)'; }}
                      onBlur={(e) => { e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.35)'; }}
                    >
                      <AppIcon name="south" style={{ fontSize: 22, lineHeight: 0 }} />
                    </button>
                  </div>
                )}
              </div>

              <form className="digitacao-area" onSubmit={enviarMensagem}>
                {/* Prévia da imagem selecionada */}
                {pendingImage && (
                  <div style={{
                    padding: '8px',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: '8px',
                    marginBottom: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                  }}>
                    <img
                      src={pendingImage.dataUrl}
                      alt="Prévia"
                      style={{
                        width: '60px',
                        height: '60px',
                        objectFit: 'cover',
                        borderRadius: '6px',
                        border: '2px solid rgba(168, 192, 217, 0.3)',
                      }}
                    />
                    <div style={{ flex: 1, fontSize: '13px', color: '#a8c0d9' }}>
                      <div style={{ fontWeight: 500 }}>{pendingImage.name}</div>
                      <div style={{ fontSize: '11px', opacity: 0.7, marginTop: '2px' }}>
                        Imagem pronta para enviar
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setPendingImage(null)}
                      style={{
                        background: 'rgba(229, 57, 53, 0.2)',
                        border: 'none',
                        borderRadius: '50%',
                        width: '28px',
                        height: '28px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(229, 57, 53, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'rgba(229, 57, 53, 0.2)';
                      }}
                      title="Remover imagem"
                    >
                      <AppIcon name="close" style={{ fontSize: '18px', color: '#e53935' }} />
                    </button>
                  </div>
                )}
                {/* Prévia do documento selecionado */}
                {pendingDocument && (
                  <div style={{
                    padding: '8px',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: '8px',
                    marginBottom: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                  }}>
                    <div style={{
                      width: '60px',
                      height: '60px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '6px',
                      border: '2px solid rgba(168, 192, 217, 0.3)',
                      backgroundColor: 'rgba(168, 192, 217, 0.1)',
                    }}>
                      <AppIcon name="description" style={{ fontSize: '32px', color: '#a8c0d9' }} />
                    </div>
                    <div style={{ flex: 1, fontSize: '13px', color: '#a8c0d9' }}>
                      <div style={{ fontWeight: 500 }}>{pendingDocument.name}</div>
                      <div style={{ fontSize: '11px', opacity: 0.7, marginTop: '2px' }}>
                        {(pendingDocument.size / 1024 / 1024).toFixed(2)} MB • Documento pronto para enviar
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setPendingDocument(null)}
                      style={{
                        background: 'rgba(229, 57, 53, 0.2)',
                        border: 'none',
                        borderRadius: '50%',
                        width: '28px',
                        height: '28px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(229, 57, 53, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'rgba(229, 57, 53, 0.2)';
                      }}
                      title="Remover documento"
                    >
                      <AppIcon name="close" style={{ fontSize: '18px', color: '#e53935' }} />
                    </button>
                  </div>
                )}
                <div className="digitacao-acoes">
                  <div className="icone-acao" onClick={() => setShowEmojiPicker(v => !v)} title="Inserir emojis" aria-label="Inserir emojis">
                    <AppIcon name="emoji_emotions" />
                  </div>
                  <div className="icone-acao" onClick={openDocumentPicker} title="Enviar documento">
                    <AppIcon name="attach_file" />
                    <input
                      ref={documentInputRef}
                      type="file"
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip,.rar"
                      style={{ display: 'none' }}
                      onChange={onDocumentSelected}
                    />
                  </div>
                  <div className="icone-acao" onClick={openImagePicker} title="Enviar imagem">
                    <AppIcon name="camera_alt" />
                    <input
                      ref={imageInputRef}
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/webp"
                      style={{ display: 'none' }}
                      onChange={onImageSelected}
                    />
                  </div>
                  <button type="button" className="icone-acao" onClick={handleMicClick} title={isRecording ? 'Parar gravação' : 'Gravar áudio'} aria-label={isRecording ? 'Parar gravação' : 'Gravar áudio'} style={{ background: 'transparent', border: 0, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <AppIcon name={isRecording ? 'stop_circle' : 'mic'} style={{ color: isRecording ? '#e53935' : 'inherit' }} />
                  </button>
                </div>
                {/* Emoji picker popover */}
                {showEmojiPicker && (
                  <div
                    ref={emojiPickerRef}
                    className="emoji-picker"
                    style={{
                      position: 'absolute',
                      bottom: '60px',
                      left: '10px',
                      background: 'var(--card-background)',
                      border: '1px solid var(--secondary-color)',
                      borderRadius: 8,
                      padding: 8,
                      zIndex: 10,
                      boxShadow: '0 6px 16px rgba(0,0,0,0.4)'
                    }}
                  >
                    <div
                      className="emoji-grid"
                      style={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: 6, maxWidth: '360px', maxHeight: '220px', overflowY: 'auto' }}
                    >
                      {EMOJIS.map((em) => (
                        <button
                          key={em}
                          type="button"
                          className="emoji-btn"
                          onClick={() => insertEmoji(em)}
                          style={{ background: 'transparent', border: 'none', fontSize: '20px', lineHeight: '24px', cursor: 'pointer' }}
                          title={em}
                        >
                          {em}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <input
                  type="text"
                  placeholder="Digite sua mensagem..."
                  value={novaMensagem}
                  onChange={e => setNovaMensagem(e.target.value)}
                  className="digitacao-input"
                  ref={inputMsgRef}
                />
                <button type="submit" className="botao-enviar">
                  <AppIcon name="send" />
                </button>
              </form>
            </>
          ) : (
            <div className="sem-conversa">
              <picture>
                <source type="image/webp" srcSet="/assets/images/3.webp" />
                <img
                  src="/assets/images/3.png"
                  alt="Bem-vindo ao atendimento"
                  className="intro-image"
                />
              </picture>
              <p>Selecione uma conversa para começar.</p>
            </div>
          )}
        </div>
      </div>
      {isLightboxOpen && (
        <div
          className="lightbox-overlay"
          onClick={closeLightbox}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '2rem',
          }}
        >
          <img
            src={lightboxSrc}
            alt="visualização"
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: '90vw',
              maxHeight: '90vh',
              width: 'auto',
              height: 'auto',
              borderRadius: '8px',
              boxShadow: '0 10px 30px rgba(0,0,0,0.5)'
            }}
          />
        </div>
      )}
    </div>
  );
};

export default AtendimentosPage;
export { AtendimentosPage };


